import { Component, ViewChild } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Platform, ToastController } from 'ionic-angular';
import { cloneDeep } from 'lodash';
import { KnownUser } from '../../../../models/user-models';
import { AppData, DelaySaveHelper } from '../../../../models/app-data/app-data';
import { CompleteDayFilters } from '../../../../models/complete-day.models';
import {
  ensureFieldPositioningLeg,
  FieldPositioningEditor,
  normalizeFieldPositioningLegSummary
} from '../../../../models/field-positioning/field-positioning.models';
import { LockDayFilters, LockedDaySummary } from '../../../../models/lock-day.models';
import { DeepWellTieIn, SeaState, SeaWaterSuction } from '../../../../models/metadata/metadata.models';
import { JackingPermissions, LockedDayPermissions } from '../../../../models/permissions/permission.models';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import {
  JackingActivityEditor,
  JackingActivityEventEditor,
  JackingActivityLegSummaryPerLegEditor,
  JackingActivityStatuses,
  JackingActivityTypes,
  JackingActivityUsedShockPadEditor,
  LocalShockPadInfo,
  LocalShockPadUsed
} from '../../../../models/projects/jacking-activity.models';
import {
  DailyLogOperationInstance,
  DailyLogType,
  DailyOperationLogRecord,
  StepInstance
} from '../../../../models/projects/operation.models';
import { ActionType, ErrorType, ValidationResult } from '../../../../models/projects/validation.models';
import { LocationOption } from '../../../../models/setup/setup.models';
import { AppDataSyncService } from '../../../../services/app-data-sync/app-data-sync.service';
import { CompleteDayService } from '../../../../services/complete-day/complete-day.service';
import { DateParserService } from '../../../../services/daily-log/daily-log-time/date-parser-service';
import { DailyLogService } from '../../../../services/daily-log/daily-log.service';
import { DateService } from '../../../../services/date/date.service';
import { DelayService } from '../../../../services/delay/delay-service/delay.service';
import { FieldPositioningService } from '../../../../services/field-positioning/field-positioning.service';
import {
  JackingActivityValidationService
} from '../../../../services/jacking-activity/jacking-activity-validation.service';
import { JackingActivityService } from '../../../../services/jacking-activity/jacking-activity.service';
import { LockedDayService } from '../../../../services/locked-day/locked-day.service';
import { MetadataService } from '../../../../services/metadata/metadata-service.service';
import { MultipleDayService } from '../../../../services/mutiple-day/multiple-day.service';
import { OperationSaveService } from '../../../../services/operations/operation-save/operation-save.service';
import { OperationService } from '../../../../services/operations/operation/operation.service';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { SetupService } from '../../../../services/setup/setup.service';
import { StepService } from '../../../../services/step/step.service';
import { TimeZoneService } from '../../../../services/time-zone/time-zone.service';
import { UserService } from '../../../../services/user/user.service';
import { VesselService } from '../../../../services/vessel/vessel.service';
import { ErrorsComponent } from '../../../shared/components/errors/errors.component';
import { JackingActivityEditCancelPage } from '../jacking-activity-edit-cancel/jacking-activity-edit-cancel';
import { JackingActivityEditCompletePage } from '../jacking-activity-edit-complete/jacking-activity-edit-complete';
import { OperationTimeService } from '../../../../services/operations/operation-time/operation-time.service';
import { TransitingSaveService } from '../../../../services/operations/transiting-save/transiting-save.service';
import { AppConfig } from '../../../../app/app.config';
import { DateTime } from 'luxon';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  JackingActivityFileUploadModalComponent
} from '../jacking-activity-file-upload-modal/jacking-activity-file-upload-modal';
import { SupportContextKey } from '../../../../models/support/support.models';
import { SupportContextService } from '../../../../services/support/support-context.service';

@IonicPage()
@Component({
  selector: 'page-jacking-activity-edit',
  templateUrl: 'jacking-activity-edit.html',
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({transform: 'translateY(-100%)'}),
        animate('200ms ease-in', style({transform: 'translateY(0%)'}))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({transform: 'translateY(-100%)'}))
      ])
    ])
  ]
})
export class JackingActivityEditPage {
  config = AppConfig.settings;

  constructor(
    public platform: Platform,
    public navCtrl: NavController,
    public navParams: NavParams,
    public dailyLogService: DailyLogService,
    public lockedDayService: LockedDayService,
    public completeDayService: CompleteDayService,
    public vesselService: VesselService,
    private userService: UserService,
    public dateService: DateService,
    public appDataSyncService: AppDataSyncService,
    public metaDataService: MetadataService,
    public jackingActivitySerivce: JackingActivityService,
    public jackingActivityValidationService: JackingActivityValidationService,
    private permissionService: PermissionsService,
    private operationService: OperationService,
    private stepService: StepService,
    private operationSaveService: OperationSaveService,
    private modalController: ModalController,
    private delayService: DelayService,
    private setupService: SetupService,
    private fieldPositioningService: FieldPositioningService,
    private timeZoneService: TimeZoneService,
    private multiDayService: MultipleDayService,
    private dateParserService: DateParserService,
    private operationTimeService: OperationTimeService,
    private transitingSaveService: TransitingSaveService,
    private toastCtrl: ToastController,
    private supportContextService: SupportContextService,
  ) {
    this.currentDate = this.navParams.get('currentDate');

    // disables any animation on page load, i.e deep well tie in notes input [@slideInOut] animation
    this.animationPaused = true;
    setTimeout(() => {
      this.animationPaused = false;
    }, 1000);
  }

  currentDate: PtLuxonDate;
  @ViewChild('frm') form;
  @ViewChild('mainContent') mainContent;
  isSaved: boolean = false;
  disableDirtyRecord: boolean = false;
  showNavigationWarning: boolean = false;
  mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  isWeb: boolean;
  loading: boolean;
  animationPaused: boolean;
  isSaving: boolean = false;
  minDate: DateTime = DateTime.local();
  maxDate: DateTime = DateTime.local();
  activitySpansMultipleDays: boolean;

  editor: JackingActivityEditor;
  editorClone: JackingActivityEditor;
  fieldPositioningEditor: FieldPositioningEditor;
  vesselClass: string;
  localShockPadInfo: LocalShockPadInfo = new LocalShockPadInfo();
  shockPadRows: number[] = [1, 2, 3, 4];
  showDeepWellTieInNotes: boolean = false;

  jackingEngineers: KnownUser[] = [];
  seaStateList: SeaState[] = [];
  deepWellTieInList: DeepWellTieIn[] = [];
  seaWaterSuctionList: SeaWaterSuction[] = [];
  locationOptions: LocationOption[] = [];
  permissions: JackingPermissions = new JackingPermissions();
  lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();

  lockedAndCompleteDayInfo: Array<{
    date: DateTime;
    dayComplete: boolean;
    locked: LockedDaySummary;
  }> = new Array<{
    date: DateTime;
    dayComplete: boolean;
    locked: LockedDaySummary;
  }>();
  isAnyDayLockedByNotYou: boolean;

  primaryOperation: DailyLogOperationInstance;
  secondaryOperation: DailyLogOperationInstance;
  validationResult: ValidationResult;

  isReadonly: boolean;
  canEditTimes: boolean;
  warnUnsavedChanges: boolean = false;
  canUploadFiles: boolean;

  ngOnInit() {
    this.supportContextService.setComponentContext(SupportContextKey.JackingActivities, 'Jacking Activity Form', 'JackingActivityEditPage');

    this.loading = true;
    this.isWeb = this.platform.is('core');
    this.ionViewEnter();
  }

  public async ionViewEnter() {
    // Get the activity from params
    this.editorClone = this.navParams.get('activity');
    this.editor = cloneDeep(this.editorClone);
    this.editor.events.sort((a, b) => a.order - b.order);
    this.validationResult = new ValidationResult();

    // Load user permissions
    this.permissions = await this.permissionService.jackingPermissions();
    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.currentDate);

    await this.calculateMinMaxDates();

    // // Get vessel type from service
    await this.vesselService.getEditor().then(vessel => {
      if (vessel) {
        if (vessel.vesselClassId === 1) {
          this.vesselClass = 'NG5500X';
        } else if (vessel.vesselClassId === 2) {
          this.vesselClass = 'NG2500X';
        } else if (vessel.vesselClassId === 3) {
          this.vesselClass = 'NG14000X';
        } else if (vessel.vesselClassId === 4) {
          this.vesselClass = 'M';
        }
      }
    });

    // Get users for signatures section
    this.userService.getKnownUsers().then(users => {
      users = users.filter(u => u.roles.some(r => r.permissions.some(p => p.name === 'Add/Amend Jacking Records')));
      this.jackingEngineers = users;
    });

    this.seaStateList = await this.metaDataService.getSeaStates();
    this.deepWellTieInList = await this.metaDataService.getDeepWellTieIns();
    this.seaWaterSuctionList = await this.metaDataService.getSeaWaterSuctionOptions();
    await this.loadOperations();
    await this.LoadLocationOptions();

    this.setupLocalShockPadCollection();

    this.minDate = await this.dateService.getStartOfDay(this.editor.startDate.dateTime);
    this.minDate = this.currentDate.dateTime > this.minDate ? this.currentDate.dateTime : this.minDate;
    this.maxDate = await this.dateService.getEndOfDay(this.editor.endDate.dateTime);

    // Load completed and locked day status foreach day(s) the activity spans
    for (let day = cloneDeep(this.minDate) as DateTime; day <= this.maxDate; day = (await this.dateService.addDuration(day, {day: 1}, true))) {
      // Get Complete Day Info
      const completeDayFilters = new CompleteDayFilters();
      completeDayFilters.date = day;

      const dayComplete = await this.completeDayService.checkDay(completeDayFilters);

      // Get Locked Day Info
      const lockDayFilters = new LockDayFilters();
      lockDayFilters.date = day;

      const lockedSummary = await this.lockedDayService.checkDay(lockDayFilters);

      this.lockedAndCompleteDayInfo.push({
        date: day,
        dayComplete: dayComplete,
        locked: lockedSummary
      });
    }
    this.isAnyDayLockedByNotYou = this.lockedAndCompleteDayInfo.some(
      a => a.locked.locked && !a.locked.lockedByYou
    );

    // Calculate if each event can be edited or not
    for (const evnt of this.editor.events) {
      const operationForEvnt = this.getOperationForJackingEvent(evnt);
      // If the event is not linked to an operation
      // then it can be edited as long as no days are locked
      evnt.canEditValues = !this.isAnyDayLockedByNotYou && (!evnt.time || !evnt.time.dateTime
        || (evnt.time.dateTime && evnt.time.dateTime >= this.currentDate.dateTime));
      if (operationForEvnt && evnt.canEditValues) {
        // If the event is linked to an operation
        // then it can be edited as long as no days are locked by another user
        // and the day it falls in hasn't been completed
        // or the day it falls in has been complete & the day is locked by the user
        // Check start date edit permissions
        const lockAndCompleteInfo = this.lockedAndCompleteDayInfo.find(a =>
          this.dateService.compareDayOffsetsPreApplied(a.date, evnt.time.dateTime)
        );
        if (lockAndCompleteInfo) {
          evnt.canEditValues =
            evnt.canEditValues &&
            (!lockAndCompleteInfo.dayComplete ||
              (lockAndCompleteInfo.dayComplete &&
                lockAndCompleteInfo.locked &&
                lockAndCompleteInfo.locked.lockedByYou));
        }
      }
      evnt.shouldUpdatePrimaryOperationStartTime = this.eventShouldUpdatePrimaryOperationStartTime(
        evnt
      );
      evnt.shouldUpdatePrimaryOperationEndTime = this.eventShouldUpdatePrimaryOperationEndTime(
        evnt
      );
      evnt.shouldUpdateSecondaryOperationStartTime = this.eventShouldUpdateSecondaryOperationStartTime(
        evnt
      );
      evnt.shouldUpdateSecondaryOperationEndTime = this.eventShouldUpdateSecondaryOperationEndTime(
        evnt
      );

      evnt.showTimeConfirmation =
        evnt.shouldUpdatePrimaryOperationStartTime ||
        evnt.shouldUpdatePrimaryOperationEndTime ||
        evnt.shouldUpdateSecondaryOperationStartTime ||
        evnt.shouldUpdateSecondaryOperationEndTime ||
        evnt.isLinkedToStep;
    }

    this.isReadonly =
      this.isAnyDayLockedByNotYou
      || this.editor.status.id === JackingActivityStatuses.Cancelled
      || !this.permissions.canManageJackingRecords
      // if you dont have the below, then everything is disabled, we then also disable further things if the user doesnt have canImpactFutureDay
      // but this is done on an input by input basis
      || !this.lockedDayPermissions.functionalPermissions.canImpactCurrentLockedDay;
    this.canEditTimes = this.lockedDayPermissions.functionalPermissions.canEditOperationTimes;
    this.checkCanUploadFiles();

    this.setDeepWellTieInNotesShow();

    this.loading = false;
  }

  // Handles dirty record to require a second confirmation
  // to leave if there are outstanding changes
  public ionViewCanLeave(): boolean {
    if (this.shouldWarnUserUnsavedChanges()) {
      this.showNavigationWarning = true;
      this.mainContent.scrollTop = 0;
      this.form.scrollToTop();
      return false;
    } else {
      return true;
    }
  }

  public shouldWarnUserUnsavedChanges() {
    return !this.disableDirtyRecord && (this.form.dirty || this.warnUnsavedChanges) && !this.isSaved && this.showNavigationWarning === false;
  }

  // Loads primary and secondary options
  private async loadOperations() {
    if (!this.editor) {
      throw new Error(`Cannot load operations for 'null' activity`);
    }

    this.primaryOperation = await this.operationService.getProjectSpecificOperationInstance(this.editor.primaryOperationId);

    if (!this.primaryOperation) {
      throw new Error('Each activity must have a primary operation');
    }

    await this.stepService.attemptJoinStepsInOperation(this.primaryOperation);

    this.secondaryOperation = await this.operationService.getProjectSpecificOperationInstance(this.editor.secondaryOperationId);

    if (this.secondaryOperation) {
      await this.stepService.attemptJoinStepsInOperation(this.secondaryOperation);
    }

    const appData = new AppData();
    appData.dailyLogOperationInstances.push(this.primaryOperation);
    if (this.secondaryOperation) {
      appData.dailyLogOperationInstances.push(this.secondaryOperation);
    }

    await this.appDataSyncService.refreshTimesToShow(appData);
  }

  async LoadLocationOptions() {
    const vesselEditor = await this.vesselService.getEditor();

    this.setupService.getSetupMetadata(vesselEditor.id, cloneDeep(this.currentDate.dateTime)).then(metadata => {
      this.setupService.getLocalSetupSummary().then(setupSummary => {

        const projectOptions = metadata.projectOptions;
        const project = projectOptions.find(
          e => e.id === setupSummary.projectId
        );

        const phaseOptions = project
          ? project.phaseOptions
          : projectOptions[0].phaseOptions;
        const phase = phaseOptions.find(e => e.id === setupSummary.phaseId);

        const stageOptions = phase
          ? phase.stageOptions
          : phaseOptions[0].stageOptions;
        const stage = stageOptions.find(e => e.id === setupSummary.stageId);

        this.locationOptions = stage
          ? stage.locationOptions
          : stageOptions[0].locationOptions;

        if (this.editor.projectInfo && this.editor.projectInfo.lastLocationId) {
          this.editor.projectInfo.lastLocation = this.locationOptions.find(e => e.id === this.editor.projectInfo.lastLocationId);
        }

        if (this.editor.projectInfo && this.editor.projectInfo.nextLocationId) {
          this.editor.projectInfo.nextLocation = this.locationOptions.find(e => e.id === this.editor.projectInfo.nextLocationId);
        }
      });
    });
  }

  openFileUploadModal() {
    const modal = this.modalController.create(JackingActivityFileUploadModalComponent, {
      jackingActivityId: this.editor.id
    }, {
      cssClass: 'jacking-activity-upload-file'
    });

    modal.present();
  }

  // Callback for Complete Button
  // Directs user to Completion Form and handles callback.
  // Marks Jacking Form as Complete.
  public async complete() {
    this.mapLocalShockPadsToEditor();
    const validation = await this.jackingActivityValidationService.CalculateMissingFields(
      this.editor
    );
    if (validation.isValid) {
      this.setStatusToComplete();
      this.excludeStepsWithoutTimes();
      this.Save();
    } else {
      const _self = this;
      const saveConfirmCallback = function (
        editor: JackingActivityEditor,
        isCancel: boolean
      ) {
        return new Promise((resolve, reject) => {
          this.disableDirtyRecord = false;
          if (isCancel) {
            resolve(null);
          } else {
            _self.editor = editor;
            _self.setStatusToComplete();
            _self.excludeStepsWithoutTimes();
            _self.Save();
            resolve(null);
          }
        });
      };
      this.disableDirtyRecord = true;
      this.navCtrl.push(
        JackingActivityEditCompletePage,
        {
          activity: this.editor,
          validation: validation,
          callback: saveConfirmCallback
        },
        {animation: 'ios-transition'}
      );
    }
  }

  // Callback for Cancel Button
  // Directs user to Cancel Form and handles callback.
  // Marks Jacking Form as Cancelled.
  public async cancel() {
    this.mapLocalShockPadsToEditor();
    const _self = this;
    const cancelCallback = function (
      editor: JackingActivityEditor,
      isCancel: boolean
    ) {
      return new Promise(async (resolve, reject) => {
        this.disableDirtyRecord = false;

        if (isCancel) {
          resolve(null);
        } else {
          _self.editor = editor;
          _self.setStatusToCancelled();
          _self.excludeStepsWithoutTimes();
          await _self.CancelOperations();
          _self.Save();
          resolve(null);
        }
      });
    };
    this.disableDirtyRecord = true;
    this.navCtrl.push(
      JackingActivityEditCancelPage,
      {
        activity: this.editor,
        callback: cancelCallback,
        primaryOperation: this.primaryOperation,
        secondaryOperation: this.secondaryOperation
      },
      {animation: 'ios-transition'}
    );
  }

  // Callback for Save button
  // Sets the Jacking Form to In Progress and Saves
  public async saveInProgress() {
    this.mapLocalShockPadsToEditor();
    this.setStatusToInProgress();
    this.Save();
  }

  // Basic Save
  private async Save() {
    await this.saveAppData();
    if (this.isSaved) {

      // after saving, update leg load data - or user will not get validation saying leg loads not completed
      await this.appDataSyncService.updateAppDataForDate(this.currentDate, null, false, true);

      this.navCtrl.pop();
    }
  }

  // Posts Jacking Activity, Operations and Field Position
  // or saves locally
  private async saveAppData() {
    this.validationResult = null;
    this.isSaved = false;

    const editorClone = cloneDeep(this.editor) as JackingActivityEditor;
    let primaryOpClone = cloneDeep(this.primaryOperation) as DailyLogOperationInstance;
    let secondaryOpClone = cloneDeep(this.secondaryOperation) as DailyLogOperationInstance;

    await this.validateJackingActivityEditor(editorClone);

    if (!this.isSaving && this.validationResult && this.validationResult.isValid) {
      this.isSaving = true;
      this.mainContent.scrollTop = 0;

      // Set the form to readonly whilst we're saving
      this.isReadonly = true;
      this.canEditTimes = false;

      const appDataBlob = new AppData();
      appDataBlob.cleanseLogs = false;

      let midnightPtLuxonDate = await this.dateService.getEndOfDayLocally(primaryOpClone.startTime.getDate());

      await this.savePrimaryOperationData(appDataBlob, primaryOpClone, midnightPtLuxonDate, editorClone);

      // ids being the same means it's the new jacking arrival port/offshore ops - so saving twice is redundant
      if (primaryOpClone?.id !== secondaryOpClone?.id) {
        await this.saveSecondaryOperationData(appDataBlob, secondaryOpClone, midnightPtLuxonDate, editorClone);
      } else {
        // this used to just be called inside saveSecondaryOperationData(), but now we sometimes don't call that method,
        // we still need to make sure these update
        if ((this.vesselClass === 'NG14000X' || this.vesselClass === 'M')) {
          this.updateXMinHoldLogRecords(appDataBlob.dailyLogRecords);
        }
      }

      // try fetching the field positioning editor if it hasn't already
      if (!this.fieldPositioningEditor) {
        this.fieldPositioningEditor = await this.fieldPositioningService.getById(this.editor.fieldPositioningEntityId, this.editor.guid);
      }

      // if it still doesn't exist, create new, otherwise update fields
      if (this.fieldPositioningEditor == null) {
        await this.createFieldPositioningEditor(appDataBlob);
      } else {
        // update end date so it gets filtered correctly in the jacking activities page
        this.fieldPositioningEditor.startDate = this.editor.startDate;
        this.fieldPositioningEditor.endDate = this.editor.endDate;
        appDataBlob.fieldPositioningEditors = [this.fieldPositioningEditor];

        await this.updateFieldPositioningFields();
      }

      appDataBlob.jackingActivities = [editorClone];

      // set times confirmed - if user hasn't already confirmed them, then they will be stuck unconfirmed otherwise
      if (editorClone.cancellationTime && editorClone.cancellationTime.dateTime) {
        for (const record of appDataBlob.dailyLogRecords) {
          record.startTimeConfirmed = true;
          record.endTimeConfirmed = true;
        }
      }

      await this.appDataSyncService.syncLocalData(appDataBlob, true);

      this.isSaved = true;
    }
  }

  // excludes Operation Steps from the Primary & Secondary
  // if the linked event does not have a time entered
  private excludeStepsWithoutTimes() {
    this.editor.events.forEach(evnt => {
      if (evnt.isLinkedToStep && !evnt.time.dateTime) {
        const steps = this.getStepsForEvent(evnt);
        if (steps) {
          steps.forEach(step => {
            step.exclude = true;
          });
        }
      }
    });
  }

  // Updates status to cancelled and un-confirms the Jacking Form
  private setStatusToInProgress() {
    this.editor.status.id = JackingActivityStatuses.InProgress;
    this.editor.status.name = 'In Progress';
    this.editor.confirmed = false;
  }

  // Updates status to complete and confirms the Jacking Form
  private setStatusToComplete() {
    this.editor.status.id = JackingActivityStatuses.Complete;
    this.editor.status.name = 'Complete';
    this.editor.confirmed = true;
  }

  // Updates status to cancelled and confirms the Jacking Form
  private setStatusToCancelled() {
    this.editor.status.id = JackingActivityStatuses.Cancelled;
    this.editor.status.name = 'Cancelled';
    this.editor.confirmed = true;
  }

  // Builds a collection of LocalShockPadUsed.
  // The new collection is used to drive the ShockPad UI
  private setupLocalShockPadCollection() {
    let maxShockPads = 0;
    if (this.vesselClass === 'NG2500X') {
      maxShockPads = 6;
    } else if (this.vesselClass === 'NG14000X' || this.vesselClass === 'M') {
      maxShockPads = 24;
    } else {
      maxShockPads = 0;
    }

    for (let i = 1; i < maxShockPads + 1; i++) {
      // leg1
      let shockPadUsed = new LocalShockPadUsed();
      shockPadUsed.shockPadNumber = i;
      shockPadUsed.isUsed = this.editor.shockPadInfo.leg1.usedShockPads.some(
        s => s.shockPadNumber === i
      );
      this.localShockPadInfo.leg1.usedShockPads.push(shockPadUsed);

      // leg2
      shockPadUsed = new LocalShockPadUsed();
      shockPadUsed.shockPadNumber = i;
      shockPadUsed.isUsed = this.editor.shockPadInfo.leg2.usedShockPads.some(
        s => s.shockPadNumber === i
      );
      this.localShockPadInfo.leg2.usedShockPads.push(shockPadUsed);

      // leg3
      shockPadUsed = new LocalShockPadUsed();
      shockPadUsed.shockPadNumber = i;
      shockPadUsed.isUsed = this.editor.shockPadInfo.leg3.usedShockPads.some(
        s => s.shockPadNumber === i
      );
      this.localShockPadInfo.leg3.usedShockPads.push(shockPadUsed);

      // leg4
      shockPadUsed = new LocalShockPadUsed();
      shockPadUsed.shockPadNumber = i;
      shockPadUsed.isUsed = this.editor.shockPadInfo.leg4.usedShockPads.some(
        s => s.shockPadNumber === i
      );
      this.localShockPadInfo.leg4.usedShockPads.push(shockPadUsed);
    }
  }

  // Helper function for saving shockpags
  private mapLocalShockPadsToEditor() {
    this.editor.shockPadInfo.leg1.usedShockPads = this.localShockPadInfo.leg1.usedShockPads
      .filter(x => x.isUsed)
      .map(x => this.mapSingleLocalShockPadToEditor(x));

    this.editor.shockPadInfo.leg2.usedShockPads = this.localShockPadInfo.leg2.usedShockPads
      .filter(x => x.isUsed)
      .map(x => this.mapSingleLocalShockPadToEditor(x));

    this.editor.shockPadInfo.leg3.usedShockPads = this.localShockPadInfo.leg3.usedShockPads
      .filter(x => x.isUsed)
      .map(x => this.mapSingleLocalShockPadToEditor(x));

    this.editor.shockPadInfo.leg4.usedShockPads = this.localShockPadInfo.leg4.usedShockPads
      .filter(x => x.isUsed)
      .map(x => this.mapSingleLocalShockPadToEditor(x));
  }

  // Helper function for saving shockpads
  private mapSingleLocalShockPadToEditor(localShockPad: LocalShockPadUsed) {
    const usedEditor = new JackingActivityUsedShockPadEditor();
    usedEditor.shockPadNumber = localShockPad.shockPadNumber;
    return usedEditor;
  }

  // Fetches the Operation based on the Jacking Event.
  // If the Jacking Event is linked to steps in the Primary Operation - the primary operation is returned
  // If the Jacking Event is linked to steps in the Secondary Operation - the secondary operation is returned
  // if jacking event was created from the new jacking arrival port/offshore operation, it doesn't matter which is returned
  private getOperationForJackingEvent(
    evnt: JackingActivityEventEditor
  ): DailyLogOperationInstance {
    if (
      this.primaryOperation.stepCollectionInstance.stepInstances.filter(
        s => s.jackingEventGuid === evnt.guid
      ).length > 0
    ) {
      return this.primaryOperation;
    }

    if (
      this.secondaryOperation &&
      this.secondaryOperation.stepCollectionInstance.stepInstances.filter(
        s => s.jackingEventGuid === evnt.guid
      ).length > 0
    ) {
      return this.secondaryOperation;
    }

    return null;
  }

  // Fetches all Operation Steps that are linked to this Jacking Event
  private getStepsForEvent(evnt: JackingActivityEventEditor): StepInstance[] {
    const operation = this.getOperationForJackingEvent(evnt);
    if (operation) {
      return operation.stepCollectionInstance.stepInstances.filter(
        s => s.jackingEventGuid === evnt.guid
      );
    } else {
      return null;
    }
  }

  // Helper function for returning the correct shock pad items
  // for each row. NG14000X / M vessels only.
  public getShockPadRow(row: number, leg: number) {
    let usedShockPads: LocalShockPadUsed[];
    if (leg === 1) {
      usedShockPads = this.localShockPadInfo.leg1.usedShockPads;
    } else if (leg === 2) {
      usedShockPads = this.localShockPadInfo.leg2.usedShockPads;
    } else if (leg === 3) {
      usedShockPads = this.localShockPadInfo.leg3.usedShockPads;
    } else if (leg === 4) {
      usedShockPads = this.localShockPadInfo.leg4.usedShockPads;
    }

    if (row === 1) {
      return usedShockPads.filter(
        sp => [1, 2, 9, 10, 17, 18].indexOf(sp.shockPadNumber) > -1
      );
    } else if (row === 2) {
      return usedShockPads.filter(
        sp => [3, 4, 11, 12, 19, 20].indexOf(sp.shockPadNumber) > -1
      );
    } else if (row === 3) {
      return usedShockPads.filter(
        sp => [5, 6, 13, 14, 21, 22].indexOf(sp.shockPadNumber) > -1
      );
    } else if (row === 4) {
      return usedShockPads.filter(
        sp => [7, 8, 15, 16, 23, 24].indexOf(sp.shockPadNumber) > -1
      );
    }
  }

  // callback function when Max Pulling Change
  public async legMaxPullingChange(
    leg: JackingActivityLegSummaryPerLegEditor,
    legName: string,
    offsetSelected?: boolean,
    offsetSelectedPtDate?: PtLuxonDate
  ) {
    const allowedTime = await this.dateService.timeAllowed(
      leg.legMaxPullingloadTimeToShow,
      leg.legMaxPullingloadTime.dateTime
        ? leg.legMaxPullingloadTime.dateTime
        : this.editor.startDate.dateTime
    );
    if (allowedTime && leg.legMaxPullingloadTimeToShow) {
      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        leg.legMaxPullingloadTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          leg.legMaxPullingloadTimeToShow,
          leg.legMaxPullingloadTime && leg.legMaxPullingloadTime.dateTime
            ? leg.legMaxPullingloadTime.dateTime
            : this.editor.startDate.dateTime
        );

        leg.legMaxPullingLoadTimeOffset = await this.timeZoneService.offsetForTime(
          leg.legMaxPullingloadTime.getDate()
        );
      } else {
        leg.legMaxPullingLoadTimeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      this.checkIfTimeIsLost(
        leg.legMaxPullingloadTimeToShow,
        leg.legMaxPullingloadTime,
        'Leg Max Pulling',
        legName
      );
    }
  }

  // callback function when Leg Free In Holes Change
  public async legFreeInHolesChange(
    leg: JackingActivityLegSummaryPerLegEditor,
    legName: string,
    offsetSelected?: boolean,
    offsetSelectedPtDate?: PtLuxonDate
  ) {
    const allowedTime = await this.dateService.timeAllowed(
      leg.legFreeInHolesExtTimeToShow,
      leg.legFreeInHolesExtTime.dateTime
        ? leg.legFreeInHolesExtTime.dateTime
        : this.editor.startDate.dateTime
    );

    if (allowedTime && leg.legFreeInHolesExtTimeToShow) {
      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        leg.legFreeInHolesExtTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          leg.legFreeInHolesExtTimeToShow,
          leg.legFreeInHolesExtTime && leg.legFreeInHolesExtTime.dateTime
            ? leg.legFreeInHolesExtTime.dateTime
            : this.editor.startDate.dateTime
        );

        leg.legFreeInHolesExtTimeOffset = await this.timeZoneService.offsetForTime(
          leg.legFreeInHolesExtTime.getDate()
        );
      } else {
        leg.legFreeInHolesExtTimeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      this.checkIfTimeIsLost(
        leg.legFreeInHolesExtTimeToShow,
        leg.legFreeInHolesExtTime,
        'Leg Free in Holes',
        legName
      );
    }
  }

  // callback function when Leg Clear Of Seabed time change
  public async legClearOfSeabedChange(
    leg: JackingActivityLegSummaryPerLegEditor,
    legName: string,
    offsetSelected?: boolean,
    offsetSelectedPtDate?: PtLuxonDate
  ) {
    const allowedTime = await this.dateService.timeAllowed(
      leg.clearOfSeaBedExtTimeToShow,
      leg.clearOfSeaBedExtTime.dateTime
        ? leg.clearOfSeaBedExtTime.dateTime
        : this.editor.startDate.dateTime
    );
    if (allowedTime && leg.clearOfSeaBedExtTimeToShow) {
      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        leg.clearOfSeaBedExtTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          leg.clearOfSeaBedExtTimeToShow,
          leg.clearOfSeaBedExtTime && leg.clearOfSeaBedExtTime.dateTime
            ? leg.clearOfSeaBedExtTime.dateTime
            : this.editor.startDate.dateTime
        );

        leg.clearOfSeabedExtTimeOffset = await this.timeZoneService.offsetForTime(
          leg.clearOfSeaBedExtTime.getDate()
        );
      } else {
        leg.clearOfSeabedExtTimeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      this.checkIfTimeIsLost(
        leg.clearOfSeaBedExtTimeToShow,
        leg.clearOfSeaBedExtTime,
        'Clear of Seabed',
        legName
      );
    }
  }

  public async finalJackingInfoSeajetsUsedTimeChange(offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(
      this.editor.finalJackingInfo.seajetsUsedTimeToShow,
      this.editor.finalJackingInfo.seajetsUsedTime.dateTime
        ? this.editor.finalJackingInfo.seajetsUsedTime.dateTime
        : this.editor.startDate.dateTime
    );

    if (allowedTime && this.editor.finalJackingInfo.seajetsUsedTimeToShow) {
      const originalTimeCopy = cloneDeep(this.editor.finalJackingInfo.seajetsUsedTime);

      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        this.editor.finalJackingInfo.seajetsUsedTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          this.editor.finalJackingInfo.seajetsUsedTimeToShow,
          this.editor.finalJackingInfo.seajetsUsedTime &&
          this.editor.finalJackingInfo.seajetsUsedTime.dateTime
            ? this.editor.finalJackingInfo.seajetsUsedTime.dateTime
            : this.editor.startDate.dateTime
        );

        this.editor.finalJackingInfo.seajetsUsedTimeOffset = await this.timeZoneService.offsetForTime(
          this.editor.finalJackingInfo.seajetsUsedTime.getDate()
        );
      } else {
        this.editor.finalJackingInfo.seajetsUsedTimeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      // stop processing if user has entered a time that has multiple offsets - this method will rerun after they select the offset
      if (this.editor.finalJackingInfo.seajetsUsedTime.offsetNull()) {
        return;
      }

      // if user is editing an event that they had already entered and was already moved onto the next day (due to the time being less than the previous event),
      // we don't want to increase the date again
      if (originalTimeCopy && !offsetSelected) {
        const sameDay = await this.dateService.compareDayOffsetsPreApplied(this.editor.finalJackingInfo.seajetsUsedTime.dateTime, originalTimeCopy.dateTime);

        if (!sameDay) {
          this.editor.finalJackingInfo.seajetsUsedTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.seajetsUsedTime, this.editor.finalJackingInfo.seajetsUsedTimeToShow, 3);
        }
      } else {
        this.editor.finalJackingInfo.seajetsUsedTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.seajetsUsedTime, this.editor.finalJackingInfo.seajetsUsedTimeToShow, 3);
      }

      this.checkIfTimeIsLost(
        this.editor.finalJackingInfo.seajetsUsedTimeToShow,
        this.editor.finalJackingInfo.seajetsUsedTime,
        'Seajets Used'
      );

      this.calculateMinMaxDates();
    } else if (!this.editor.finalJackingInfo.seajetsUsedTimeToShow) {
      this.editor.finalJackingInfo.seajetsUsedTime.dateTime = null;
    }
  }

  public async finalJackingInfoDeepWellTieInDepthTimeChange(offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(
      this.editor.finalJackingInfo.deepWellTieInDepthTimeToShow,
      this.editor.finalJackingInfo.deepWellTieInDepthTime.dateTime
        ? this.editor.finalJackingInfo.deepWellTieInDepthTime.dateTime
        : this.editor.startDate.dateTime
    );

    if (allowedTime && this.editor.finalJackingInfo.deepWellTieInDepthTimeToShow) {
      const originalTimeCopy = cloneDeep(this.editor.finalJackingInfo.deepWellTieInDepthTime);

      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        this.editor.finalJackingInfo.deepWellTieInDepthTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          this.editor.finalJackingInfo.deepWellTieInDepthTimeToShow,
          this.editor.finalJackingInfo.deepWellTieInDepthTime &&
          this.editor.finalJackingInfo.deepWellTieInDepthTime.dateTime
            ? this.editor.finalJackingInfo.deepWellTieInDepthTime.dateTime
            : this.editor.startDate.dateTime
        );

        this.editor.finalJackingInfo.deepWellTieInDepthTimeOffset = await this.timeZoneService.offsetForTime(
          this.editor.finalJackingInfo.deepWellTieInDepthTime.getDate()
        );
      } else {
        this.editor.finalJackingInfo.deepWellTieInDepthTimeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      // stop processing if user has entered a time that has multiple offsets - this method will rerun after they select the offset
      if (this.editor.finalJackingInfo.deepWellTieInDepthTime.offsetNull()) {
        return;
      }

      // if user is editing an event that they had already entered and was already moved onto the next day (due to the time being less than the previous event),
      // we don't want to increase the date again
      if (originalTimeCopy && !offsetSelected) {
        const sameDay = await this.dateService.compareDayOffsetsPreApplied(this.editor.finalJackingInfo.deepWellTieInDepthTime.dateTime, originalTimeCopy.dateTime);

        if (!sameDay) {
          this.editor.finalJackingInfo.deepWellTieInDepthTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.deepWellTieInDepthTime, this.editor.finalJackingInfo.deepWellTieInDepthTimeToShow, 4);
        }
      } else {
        this.editor.finalJackingInfo.deepWellTieInDepthTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.deepWellTieInDepthTime, this.editor.finalJackingInfo.deepWellTieInDepthTimeToShow, 4);
      }

      this.checkIfTimeIsLost(
        this.editor.finalJackingInfo.deepWellTieInDepthTimeToShow,
        this.editor.finalJackingInfo.deepWellTieInDepthTime,
        'Deepwell Tie In Depth'
      );

      this.calculateMinMaxDates();
    } else if (!this.editor.finalJackingInfo.deepWellTieInDepthTimeToShow) {
      this.editor.finalJackingInfo.deepWellTieInDepthTime.dateTime = null;
    }
  }

  public async finalJackingInfoDeepWellTieInNumberTimeChange(offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(
      this.editor.finalJackingInfo.deepWellTieInNumberTimeToShow,
      this.editor.finalJackingInfo.deepWellTieInNumberTime.dateTime || this.editor.startDate.dateTime
    );

    if (allowedTime && this.editor.finalJackingInfo.deepWellTieInNumberTimeToShow) {
      const originalTimeCopy = cloneDeep(this.editor.finalJackingInfo.deepWellTieInNumberTime);

      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        this.editor.finalJackingInfo.deepWellTieInNumberTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          this.editor.finalJackingInfo.deepWellTieInNumberTimeToShow,
          this.editor.finalJackingInfo.deepWellTieInNumberTime &&
          this.editor.finalJackingInfo.deepWellTieInNumberTime.dateTime
            ? this.editor.finalJackingInfo.deepWellTieInNumberTime.dateTime
            : this.editor.startDate.dateTime
        );

        this.editor.finalJackingInfo.deepWellTieInNumberTimeOffset = await this.timeZoneService.offsetForTime(
          this.editor.finalJackingInfo.deepWellTieInNumberTime.getDate()
        );
      } else {
        this.editor.finalJackingInfo.deepWellTieInNumberTimeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      // stop processing if user has entered a time that has multiple offsets - this method will rerun after they select the offset
      if (this.editor.finalJackingInfo.deepWellTieInNumberTime.offsetNull()) {
        return;
      }

      // if user is editing an event that they had already entered and was already moved onto the next day (due to the time being less than the previous event),
      // we don't want to increase the date again
      if (originalTimeCopy && !offsetSelected) {
        const sameDay = await this.dateService.compareDayOffsetsPreApplied(this.editor.finalJackingInfo.deepWellTieInNumberTime.dateTime, originalTimeCopy.dateTime);

        if (!sameDay) {
          this.editor.finalJackingInfo.deepWellTieInNumberTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.deepWellTieInNumberTime, this.editor.finalJackingInfo.deepWellTieInNumberTimeToShow, 5);
        }
      } else {
        this.editor.finalJackingInfo.deepWellTieInNumberTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.deepWellTieInNumberTime, this.editor.finalJackingInfo.deepWellTieInNumberTimeToShow, 5);
      }

      this.checkIfTimeIsLost(
        this.editor.finalJackingInfo.deepWellTieInNumberTimeToShow,
        this.editor.finalJackingInfo.deepWellTieInNumberTime,
        'Deepwell Tie In Number'
      );

      this.calculateMinMaxDates();
    } else if (!this.editor.finalJackingInfo.deepWellTieInNumberTimeToShow) {
      this.editor.finalJackingInfo.deepWellTieInNumberTime.dateTime = null;
    }
  }

  public async finalJackingInfoFinalAirGapTimeChange(offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(
      this.editor.finalJackingInfo.finalAirGapTimeToShow,
      this.editor.finalJackingInfo.finalAirGapTime.dateTime || this.editor.startDate.dateTime
    );

    if (allowedTime && this.editor.finalJackingInfo.finalAirGapTimeToShow) {
      const originalTimeCopy = cloneDeep(this.editor.finalJackingInfo.finalAirGapTime);

      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        this.editor.finalJackingInfo.finalAirGapTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          this.editor.finalJackingInfo.finalAirGapTimeToShow,
          this.editor.finalJackingInfo.finalAirGapTime &&
          this.editor.finalJackingInfo.finalAirGapTime.dateTime
            ? this.editor.finalJackingInfo.finalAirGapTime.dateTime
            : this.editor.startDate.dateTime
        );

        this.editor.finalJackingInfo.finalAirGapTimeOffset = await this.timeZoneService.offsetForTime(
          this.editor.finalJackingInfo.finalAirGapTime.getDate()
        );
      } else {
        this.editor.finalJackingInfo.finalAirGapTimeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      // stop processing if user has entered a time that has multiple offsets - this method will rerun after they select the offset
      if (this.editor.finalJackingInfo.finalAirGapTime.offsetNull()) {
        return;
      }

      // if user is editing an event that they had already entered and was already moved onto the next day (due to the time being less than the previous event),
      // we don't want to increase the date again
      if (originalTimeCopy && !offsetSelected) {
        const sameDay = await this.dateService.compareDayOffsetsPreApplied(this.editor.finalJackingInfo.finalAirGapTime.dateTime, originalTimeCopy.dateTime);

        if (!sameDay) {
          this.editor.finalJackingInfo.finalAirGapTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.finalAirGapTime, this.editor.finalJackingInfo.finalAirGapTimeToShow, 6);
        }
      } else {
        this.editor.finalJackingInfo.finalAirGapTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.finalAirGapTime, this.editor.finalJackingInfo.finalAirGapTimeToShow, 6);
      }

      if (this.secondaryOperation) {
        // Set the end time of the jack up operation
        this.secondaryOperation.endTime = cloneDeep(
          this.editor.finalJackingInfo.finalAirGapTime
        );
        this.secondaryOperation.endTimeOffset = this.editor.finalJackingInfo.finalAirGapTimeOffset;
        this.secondaryOperation.endTimeToShow = this.editor.finalJackingInfo.finalAirGapTimeToShow;

        if (this.secondaryOperation.id === this.primaryOperation.id) {
          this.cloneSecondaryEndTimeForPrimaryOperation();
        }
      }

      // Set the end time of the activity
      this.editor.endDate = cloneDeep(
        this.editor.finalJackingInfo.finalAirGapTime
      );
      this.editor.showEndDate = await this.dateService.setFullTimeString(this.editor.endDate.dateTime);

      this.checkIfTimeIsLost(
        this.editor.finalJackingInfo.finalAirGapTimeToShow,
        this.editor.finalJackingInfo.finalAirGapTime,
        'Final Air Gap'
      );

      this.calculateMinMaxDates();
    } else if (!this.editor.finalJackingInfo.finalAirGapTimeToShow) {
      this.editor.finalJackingInfo.finalAirGapTime.dateTime = null;
    }
  }

  public async finalJackingInfoFinalAirGapTimeConfirmationToggle() {
    this.editor.finalJackingInfo.finalAirGapTimeConfirmed = !this.editor
      .finalJackingInfo.finalAirGapTimeConfirmed;
    if (this.secondaryOperation) {
      this.secondaryOperation.endConfirmed = this.editor.finalJackingInfo.finalAirGapTimeConfirmed;

      if (this.secondaryOperation.id === this.primaryOperation.id) {
        // update primary as only the primary op is saved if ids are equal
        this.primaryOperation.endConfirmed = cloneDeep(this.secondaryOperation.endConfirmed);
      }
    }
  }

  public async finalJackingInfoDavitPumpUsedTimeChange(offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(
      this.editor.finalJackingInfo.davitPumpUsedTimeToShow,
      this.editor.finalJackingInfo.davitPumpUsedTime.dateTime
        ? this.editor.finalJackingInfo.davitPumpUsedTime.dateTime
        : this.editor.startDate.dateTime
    );

    if (allowedTime && this.editor.finalJackingInfo.davitPumpUsedTimeToShow) {
      const originalTimeCopy = cloneDeep(this.editor.finalJackingInfo.davitPumpUsedTime);

      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        this.editor.finalJackingInfo.davitPumpUsedTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          this.editor.finalJackingInfo.davitPumpUsedTimeToShow,
          this.editor.finalJackingInfo.davitPumpUsedTime &&
          this.editor.finalJackingInfo.davitPumpUsedTime.dateTime
            ? this.editor.finalJackingInfo.davitPumpUsedTime.dateTime
            : this.editor.startDate.dateTime
        );

        this.editor.finalJackingInfo.davitPumpUsedTimeOffset = await this.timeZoneService.offsetForTime(
          this.editor.finalJackingInfo.davitPumpUsedTime.getDate()
        );
      } else {
        this.editor.finalJackingInfo.davitPumpUsedTimeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      // stop processing if user has entered a time that has multiple offsets - this method will rerun after they select the offset
      if (this.editor.finalJackingInfo.davitPumpUsedTime.offsetNull()) {
        return;
      }

      // if user is editing an event that they had already entered and was already moved onto the next day (due to the time being less than the previous event),
      // we don't want to increase the date again
      if (originalTimeCopy && !offsetSelected) {
        const sameDay = await this.dateService.compareDayOffsetsPreApplied(this.editor.finalJackingInfo.davitPumpUsedTime.dateTime, originalTimeCopy.dateTime);

        if (!sameDay) {
          this.editor.finalJackingInfo.davitPumpUsedTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.davitPumpUsedTime, this.editor.finalJackingInfo.davitPumpUsedTimeToShow, 7);
        }
      } else {
        this.editor.finalJackingInfo.davitPumpUsedTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.davitPumpUsedTime, this.editor.finalJackingInfo.davitPumpUsedTimeToShow, 7);
      }

      this.checkIfTimeIsLost(
        this.editor.finalJackingInfo.davitPumpUsedTimeToShow,
        this.editor.finalJackingInfo.davitPumpUsedTime,
        'Davit Pump in use'
      );

      this.calculateMinMaxDates();
    } else if (!this.editor.finalJackingInfo.davitPumpUsedTimeToShow) {
      this.editor.finalJackingInfo.davitPumpUsedTime.dateTime = null;
    }
  }

  public async finalJackingInfoFinalPenetrationsTimeChange(offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(
      this.editor.finalJackingInfo.finalPenetrationsTimeToShow,
      this.editor.finalJackingInfo.finalPenetrationsTime.dateTime || this.editor.startDate.dateTime
    );

    if (allowedTime && this.editor.finalJackingInfo.finalPenetrationsTimeToShow) {
      const originalTimeCopy = cloneDeep(this.editor.finalJackingInfo.finalPenetrationsTime);

      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        this.editor.finalJackingInfo.finalPenetrationsTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          this.editor.finalJackingInfo.finalPenetrationsTimeToShow,
          this.editor.finalJackingInfo.finalPenetrationsTime &&
          this.editor.finalJackingInfo.finalPenetrationsTime.dateTime
            ? this.editor.finalJackingInfo.finalPenetrationsTime.dateTime
            : this.editor.startDate.dateTime
        );

        this.editor.finalJackingInfo.finalPenetrationsTimeOffset = await this.timeZoneService.offsetForTime(
          this.editor.finalJackingInfo.finalPenetrationsTime.getDate()
        );
      } else {
        this.editor.finalJackingInfo.finalPenetrationsTimeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      // stop processing if user has entered a time that has multiple offsets - this method will rerun after they select the offset
      if (this.editor.finalJackingInfo.finalPenetrationsTime.offsetNull()) {
        return;
      }

      // if user is editing an event that they had already entered and was already moved onto the next day (due to the time being less than the previous event),
      // we don't want to increase the date again
      // final values are ordered differently depending on vessel class
      const finalValuesIndex = (this.vesselClass === 'NG14000X' || this.vesselClass === 'M') ? 2 : 0;
      if (originalTimeCopy && originalTimeCopy.dateTime && !offsetSelected) {
        const sameDay = await this.dateService.compareDayOffsetsPreApplied(this.editor.finalJackingInfo.finalPenetrationsTime.dateTime, originalTimeCopy.dateTime);

        if (!sameDay) {
          this.editor.finalJackingInfo.finalPenetrationsTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.finalPenetrationsTime, this.editor.finalJackingInfo.finalPenetrationsTimeToShow, finalValuesIndex);
        }
      } else {
        this.editor.finalJackingInfo.finalPenetrationsTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.finalPenetrationsTime, this.editor.finalJackingInfo.finalPenetrationsTimeToShow, finalValuesIndex);
      }

      this.checkIfTimeIsLost(
        this.editor.finalJackingInfo.finalPenetrationsTimeToShow,
        this.editor.finalJackingInfo.finalPenetrationsTime,
        'Final Penetrations'
      );

      this.calculateMinMaxDates();
    } else if (!this.editor.finalJackingInfo.finalPenetrationsTimeToShow) {
      this.editor.finalJackingInfo.finalPenetrationsTime.dateTime = null;
    }
  }

  public async finalJackingInfoDifferenceFromExpectedTimeChange(offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(
      this.editor.finalJackingInfo.differenceFromExpectedTimeToShow,
      this.editor.finalJackingInfo.differenceFromExpectedTime.dateTime
        ? this.editor.finalJackingInfo.differenceFromExpectedTime.dateTime
        : this.editor.startDate.dateTime
    );

    if (allowedTime && this.editor.finalJackingInfo.differenceFromExpectedTimeToShow) {
      // need to take this for use below when checking if event should be next day
      const originalTimeCopy = cloneDeep(this.editor.finalJackingInfo.differenceFromExpectedTime);

      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        this.editor.finalJackingInfo.differenceFromExpectedTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          this.editor.finalJackingInfo.differenceFromExpectedTimeToShow,
          this.editor.finalJackingInfo.differenceFromExpectedTime &&
          this.editor.finalJackingInfo.differenceFromExpectedTime.dateTime
            ? this.editor.finalJackingInfo.differenceFromExpectedTime.dateTime
            : this.editor.startDate.dateTime
        );

        this.editor.finalJackingInfo.differenceFromExpectedTimeOffset = await this.timeZoneService.offsetForTime(
          this.editor.finalJackingInfo.differenceFromExpectedTime.getDate()
        );
      } else {
        this.editor.finalJackingInfo.differenceFromExpectedTimeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      // stop processing if user has entered a time that has multiple offsets - this method will rerun after they select the offset
      if (this.editor.finalJackingInfo.differenceFromExpectedTime.offsetNull()) {
        return;
      }

      // if user is editing an event that they had already entered and was already moved onto the next day (due to the time being less than the previous event),
      // we don't want to increase the date again
      if (originalTimeCopy && !offsetSelected) {
        const sameDay = await this.dateService.compareDayOffsetsPreApplied(this.editor.finalJackingInfo.differenceFromExpectedTime.dateTime, originalTimeCopy.dateTime);

        if (!sameDay) {
          this.editor.finalJackingInfo.differenceFromExpectedTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.differenceFromExpectedTime, this.editor.finalJackingInfo.differenceFromExpectedTimeToShow, 1);
        }
      } else {
        this.editor.finalJackingInfo.differenceFromExpectedTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.differenceFromExpectedTime, this.editor.finalJackingInfo.differenceFromExpectedTimeToShow, 1);
      }

      this.checkIfTimeIsLost(
        this.editor.finalJackingInfo.differenceFromExpectedTimeToShow,
        this.editor.finalJackingInfo.differenceFromExpectedTime,
        'Difference From Expected'
      );

      this.calculateMinMaxDates();
    } else if (!this.editor.finalJackingInfo.differenceFromExpectedTimeToShow) {
      this.editor.finalJackingInfo.differenceFromExpectedTime.dateTime = null;
    }
  }

  public async finalJackingInfoFinalValuesTimeChange(offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(
      this.editor.finalJackingInfo.finalLegValuesTimeToShow,
      this.editor.finalJackingInfo.finalLegValuesTime.dateTime
        ? this.editor.finalJackingInfo.finalLegValuesTime.dateTime
        : this.editor.startDate.dateTime
    );

    if (allowedTime && this.editor.finalJackingInfo.finalLegValuesTimeToShow) {
      const originalTimeCopy = cloneDeep(this.editor.finalJackingInfo.finalLegValuesTime);

      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        this.editor.finalJackingInfo.finalLegValuesTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          this.editor.finalJackingInfo.finalLegValuesTimeToShow,
          this.editor.finalJackingInfo.finalLegValuesTime &&
          this.editor.finalJackingInfo.finalLegValuesTime.dateTime
            ? this.editor.finalJackingInfo.finalLegValuesTime.dateTime
            : this.editor.startDate.dateTime
        );

        this.editor.finalJackingInfo.finalLegValuesTimeOffset = await this.timeZoneService.offsetForTime(
          this.editor.finalJackingInfo.finalLegValuesTime.getDate()
        );
      } else {
        this.editor.finalJackingInfo.finalLegValuesTimeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      // stop processing if user has entered a time that has multiple offsets - this method will rerun after they select the offset
      if (this.editor.finalJackingInfo.finalLegValuesTime.offsetNull()) {
        return;
      }

      // if user is editing an event that they had already entered and was already moved onto the next day (due to the time being less than the previous event),
      // we don't want to increase the date again

      // final values are ordered differently depending on vessel class
      const finalValuesIndex = (this.vesselClass === 'NG14000X' || this.vesselClass === 'M') ? 0 : 2;
      if (originalTimeCopy && !offsetSelected) {
        const sameDay = await this.dateService.compareDayOffsetsPreApplied(this.editor.finalJackingInfo.finalLegValuesTime.dateTime, originalTimeCopy.dateTime);

        if (!sameDay) {
          this.editor.finalJackingInfo.finalLegValuesTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.finalLegValuesTime, this.editor.finalJackingInfo.finalLegValuesTimeToShow, finalValuesIndex);
        }
      } else {
        this.editor.finalJackingInfo.finalLegValuesTime = await this.checkIfFinalValueShouldBeNextDay(this.editor.finalJackingInfo.finalLegValuesTime, this.editor.finalJackingInfo.finalLegValuesTimeToShow, finalValuesIndex);
      }

      this.checkIfTimeIsLost(
        this.editor.finalJackingInfo.finalLegValuesTimeToShow,
        this.editor.finalJackingInfo.finalLegValuesTime,
        'Final Leg Values'
      );

      this.calculateMinMaxDates();
    } else if (!this.editor.finalJackingInfo.finalLegValuesTimeToShow) {
      this.editor.finalJackingInfo.finalLegValuesTime.dateTime = null;
    }
  }

  public async legsFreeTimeChange(offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(
      this.editor.endTimeToShow,
      this.editor.endDate.dateTime
        ? this.editor.endDate.dateTime
        : this.editor.startDate.dateTime
    );

    if (allowedTime && this.editor.endTimeToShow !== null) {
      const originalTimeCopy = cloneDeep(this.editor.endDate) as PtLuxonDate;

      if (!offsetSelected) {
        this.editor.endDate = await this.dateService.setPtLuxonDateFromUserEnteredString(
          this.editor.endTimeToShow,
          this.editor.endDate && this.editor.endDate.dateTime
            ? this.editor.endDate.dateTime
            : this.editor.startDate.dateTime
        );
      } else {
        this.editor.endOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      // stop processing if user has entered a time that has multiple offsets - this method will rerun after they select the offset
      if (this.editor.endDate.offsetNull()) {
        return;
      }

      if (!offsetSelected) {
        this.editor.endOffset = await this.timeZoneService.offsetForTime(
          this.editor.endDate.getDate()
        );
      }

      if (originalTimeCopy && !offsetSelected && this.editor.endDate && this.editor.endDate.dateTime) {
        await this.checkIfLegsFreeTimeShouldBeNextDay();
      }

      this.primaryOperation.endTime = cloneDeep(this.editor.endDate);
      this.primaryOperation.endTimeToShow = cloneDeep(this.editor.endTimeToShow);
      this.primaryOperation.endTimeOffset = cloneDeep(this.editor.endOffset);

      this.checkIfTimeIsLost(
        this.editor.endTimeToShow,
        this.editor.endDate,
        'Legs Free Time'
      );

      await this.calculateMinMaxDates();
    }
  }

  public async toggleLegsFreeTimeConfirmed() {
    this.editor.endTimeConfirmed = !this.editor.endTimeConfirmed;
    this.primaryOperation.endConfirmed = this.editor.endTimeConfirmed;
  }

  // callback function for when an event time is modified
  // changes are rippled through to linked operation steps
  // and operation start/end times as appropriate.
  public async eventTimeChange(evnt: JackingActivityEventEditor, offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(
      evnt.timeToShow,
      evnt.time.dateTime || this.editor.startDate.dateTime
    );

    if (allowedTime && evnt.timeToShow !== null) {
      const originalTimeCopy = cloneDeep(evnt.time) as PtLuxonDate; // need to take this for use below when checking if event should be next day

      // only difference in logic pathway, when either: entering a time, or selecting an offset - is the following if statement, the rest of the
      // logic, ie around dates changing should all be the same i think - this removes the need for separate methods for each of these, but they may need
      // their own methods if problems arise
      if (!offsetSelected) {
        evnt.time = await this.dateService.setPtLuxonDateFromUserEnteredString(
          evnt.timeToShow,
          evnt.time && evnt.time.dateTime
            ? evnt.time.dateTime
            : this.currentDate && this.currentDate.dateTime > this.editor.startDate.dateTime
              ? this.currentDate.dateTime
              : this.editor.startDate.dateTime
        );
      } else {
        evnt.timeOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      // stop processing if user has entered a time that has multiple offsets - this method will rerun after they select the offset
      if (evnt.time.offsetNull()) {
        return;
      }

      // if user is editing an event that they had already entered and was already moved onto the next day (due to the time being less than the previous event),
      // we don't want to increase the date again
      if (originalTimeCopy && originalTimeCopy.dateTime && !offsetSelected) {
        const sameDay = await this.dateService.compareDayOffsetsPreApplied(evnt.time.dateTime, originalTimeCopy.dateTime);

        if (!sameDay) {
          await this.checkIfEventShouldBeNextDay(evnt);
        }
      } else {
        await this.checkIfEventShouldBeNextDay(evnt);
      }

      // ensure this comes after the above check for next day, or the offset may become outdated when the time gets moved
      if (!offsetSelected) {
        evnt.timeOffset = await this.timeZoneService.offsetForTime(
          evnt.time.getDate()
        );
      }

      this.checkIfTimeIsLost(
        evnt.timeToShow,
        evnt.time,
        `Event: ${evnt.description}`
      );

      if (
        this.editor.fieldPositioningEntityId &&
        (((this.vesselClass === 'NG14000X' || this.vesselClass === 'M') && evnt.description === 'Pin') ||
          (this.vesselClass === 'NG5500X' && evnt.description === 'Pinned') ||
          (this.vesselClass === 'NG2500X' && evnt.description === 'Pinned'))
      ) {
        // If we don't have field positioning editor go fetch
        if (!this.fieldPositioningEditor) {
          this.fieldPositioningEditor = await this.fieldPositioningService.getById(this.editor.fieldPositioningEntityId, this.editor.guid);
        }

        // If we now have the editor update pinned time and time to show
        if (this.fieldPositioningEditor) {
          this.fieldPositioningEditor.pinnedTime = cloneDeep(
            evnt.time
          );
          this.fieldPositioningEditor.showPinnedTime = evnt.timeToShow;
        }
      }

      // Update each step start/end times to be the new time
      const steps = await this.getStepsForEvent(evnt);
      if (steps) {
        for (const step of steps) {
          step.startTime = cloneDeep(evnt.time);
          step.endTime = cloneDeep(evnt.time);
          step.startTimeToShow = evnt.timeToShow;
          step.endTimeToShow = evnt.timeToShow;
          step.startTimeOffset = evnt.timeOffset;
          step.endTimeOffset = evnt.timeOffset;
        }
      }

      // Update any operation level fields as appropriate
      if (evnt.shouldUpdatePrimaryOperationStartTime) {
        this.primaryOperation.startTime = cloneDeep(evnt.time);
        this.primaryOperation.startTimeToShow = evnt.timeToShow;
        this.primaryOperation.startTimeOffset = evnt.timeOffset;
        this.editor.startDate = cloneDeep(evnt.time);
        this.editor.showStartDate = await this.dateService.setFullTimeString(this.editor.startDate.dateTime);
      }

      if (evnt.shouldUpdatePrimaryOperationEndTime) {
        this.primaryOperation.endTime = cloneDeep(evnt.time);
        this.primaryOperation.endTimeToShow = cloneDeep(evnt.timeToShow);
        this.primaryOperation.endTimeOffset = cloneDeep(evnt.timeOffset);
      }

      if (evnt.shouldUpdateSecondaryOperationStartTime) {
        if (this.secondaryOperation) {
          this.secondaryOperation.startTime = cloneDeep(evnt.time);
          this.secondaryOperation.startTimeToShow = evnt.timeToShow;
          this.secondaryOperation.startTimeOffset = evnt.timeOffset;
        }

        if (!this.fieldPositioningEditor) {
          this.fieldPositioningEditor = await this.fieldPositioningService.getById(this.editor.fieldPositioningEntityId, this.editor.guid);
        }

        // If we now have the editor update pinned time and time to show
        if (this.fieldPositioningEditor) {
          this.fieldPositioningEditor.startDate = cloneDeep(
            evnt.time
          );
          this.fieldPositioningEditor.showStartDate = evnt.timeToShow;
        }
      }

      if (evnt.shouldUpdateSecondaryOperationEndTime) {
        if (this.secondaryOperation) {
          this.secondaryOperation.endTime = cloneDeep(evnt.time);
          this.secondaryOperation.endTimeToShow = evnt.timeToShow;
          this.secondaryOperation.endTimeOffset = evnt.timeOffset;
          this.editor.endDate = cloneDeep(evnt.time);
          this.editor.showEndDate = await this.dateService.setFullTimeString(this.editor.endDate.dateTime);
          this.editor.endOffset = await this.timeZoneService.offsetForTime(
            this.editor.endDate.getDate()
          );

          if (this.secondaryOperation.id === this.primaryOperation.id) {
            this.cloneSecondaryEndTimeForPrimaryOperation();
          }
        }

        if (!this.fieldPositioningEditor) {
          this.fieldPositioningEditor = await this.fieldPositioningService.getById(this.editor.fieldPositioningEntityId, this.editor.guid);
        }

        // If we now have the editor update pinned time and time to show
        if (this.fieldPositioningEditor) {
          this.fieldPositioningEditor.endDate = cloneDeep(evnt.time);
          this.fieldPositioningEditor.showEndDate = evnt.timeToShow;
        }
      }
    }
  }

  // callback function for when an event time is confirmed or unconfirmed
  // changes are rippled through to linked operation steps
  // and operation start/end times as appropriate.
  public async toggleTimeConfirmed(evnt: JackingActivityEventEditor) {
    evnt.timeConfirmed = !evnt.timeConfirmed;
    const steps = this.getStepsForEvent(evnt);
    if (steps) {
      for (const step of steps) {
        step.startConfirmed = evnt.timeConfirmed;
        step.endConfirmed = evnt.timeConfirmed;
      }
    }

    // Update any operation level fields as appropriate
    if (evnt.shouldUpdatePrimaryOperationStartTime) {
      this.primaryOperation.startConfirmed = evnt.timeConfirmed;
    }
    if (evnt.shouldUpdatePrimaryOperationEndTime) {
      this.primaryOperation.endConfirmed = evnt.timeConfirmed;
    }
    if (evnt.shouldUpdateSecondaryOperationStartTime) {
      if (this.secondaryOperation) {
        this.secondaryOperation.startConfirmed = evnt.timeConfirmed;
      }
    }
    if (evnt.shouldUpdateSecondaryOperationEndTime) {
      if (this.secondaryOperation) {
        this.secondaryOperation.endConfirmed = evnt.timeConfirmed;

        if (this.secondaryOperation.id === this.primaryOperation.id) {
          // update primary as only the primary op is saved if ids are equal
          this.primaryOperation.endConfirmed = cloneDeep(this.secondaryOperation.endConfirmed);
        }
      }
    }
  }

  // Helper function for Deep Well Tie in dropdown to make sure that values
  // get bound correctly.
  public compareDeepWellTieIns(d1: DeepWellTieIn, d2: DeepWellTieIn): boolean {
    return d1.id === d2.id;
  }

  // creates a new delay operation and uses this to cancel the operations linked
  // to the jacking form
  private async CancelOperations() {
    const delayOp = new DailyLogOperationInstance();
    delayOp.startTime = cloneDeep(this.editor.cancellationTime);
    delayOp.startTimeOffset = this.editor.cancellationTime && this.editor.cancellationTime.dateTime
      ? this.editor.cancellationTime.dateTime.offset / 60
      : 0;
    delayOp.endTime = cloneDeep(this.editor.cancellationTime);
    delayOp.endTimeOffset = cloneDeep(delayOp.startTimeOffset);
    const delayOpCopy = cloneDeep(delayOp);
    const operationsToCancel: DailyLogOperationInstance[] = [];

    // just push the primary op if the ids are equal, or it will cancel the same operation twice
    if (this.editor.type.id === JackingActivityTypes.Arrival && this.secondaryOperation && this.secondaryOperation.id !== this.primaryOperation.id) {
      // Only cancel the first half if the delay affects it
      if (this.dateService.isFirstDateLargerOffsetsPreApplied(this.primaryOperation.endTime.dateTime, delayOp.startTime.dateTime)) {
        operationsToCancel.push(this.primaryOperation);
      } else if (!this.secondaryOperation) {
        // or if secondary op is null, then user may be cancelling outside of the jacking arrival op time, but we still want the delay to cancel it
        this.primaryOperation.endTime = cloneDeep(delayOpCopy.startTime);
        operationsToCancel.push(this.primaryOperation);
      }

      // set secondaryOp end time to delay start (cancellation time) - this ensures other parts of the system eg validation still work correctly
      if (this.secondaryOperation) {
        this.secondaryOperation.endTime = cloneDeep(delayOpCopy.startTime);
        this.secondaryOperation.endTimeOffset = cloneDeep(delayOpCopy.startTimeOffset);
        this.secondaryOperation.endTimeToShow = cloneDeep(delayOpCopy.startTimeToShow);

        if (this.secondaryOperation.id === this.primaryOperation.id) {
          this.cloneSecondaryEndTimeForPrimaryOperation();
        }
        operationsToCancel.push(this.secondaryOperation);
      }
    } else {
      operationsToCancel.push(this.primaryOperation);
    }

    let delaySaveHelper = new DelaySaveHelper();

    delaySaveHelper.dailyLogDate = this.currentDate.dateTime;

    for (let i = 0; i < operationsToCancel.length; i++) {
      delaySaveHelper = await this.delayService.cancelOperation(delayOp, operationsToCancel[i], delaySaveHelper, true);

      const locationChangeAppData = await this.tryUpdateLocationChangeRecord(operationsToCancel[i]);

      if (locationChangeAppData && locationChangeAppData.dailyLogRecords) {
        delaySaveHelper.affectedOperationDailyLogRecords = delaySaveHelper.affectedOperationDailyLogRecords.concat(locationChangeAppData.dailyLogRecords);
      }

      if (locationChangeAppData && locationChangeAppData.setupChangeLogRecords) {
        delaySaveHelper.setupChangeLogRecords = delaySaveHelper.setupChangeLogRecords.concat(locationChangeAppData.setupChangeLogRecords);
      }
    }

    // call to save all changes
    await this.delayService.saveDelay(cloneDeep(delaySaveHelper));
  }

  private async tryUpdateLocationChangeRecord(
    operation: DailyLogOperationInstance,
    oldOperationDailyLogs?: DailyOperationLogRecord[],
    datesAmbiguous?: boolean
  ): Promise<AppData> {
    const operationClone = cloneDeep(operation) as DailyLogOperationInstance;
    const appData = new AppData();
    let chosenPtDate = operationClone.endTime;
    let chosenTimeToShow = operationClone.endTimeToShow;

    if (!oldOperationDailyLogs) {
      oldOperationDailyLogs = await this.appDataSyncService.getLogsForOperation(
        operationClone.id
      );
    }

    // as operation end time stays as the original endtime when an operation is cancelled, we need to manually override with the cancel time
    if (operation.cancelledTime) {
      operation.cancelledTimePtDate = new PtLuxonDate(operation.cancelledTime);
      chosenPtDate = operation.cancelledTimePtDate;
      chosenTimeToShow = await this.dateService.setTimeStringFromDateTime(chosenPtDate.dateTime);
    }

    // Find location change records and adjust date
    const locationChangeRec = oldOperationDailyLogs.find(
      e => e.typeId === DailyLogType.LocationChange
    );

    if (locationChangeRec) {
      locationChangeRec.startTimeShow = chosenTimeToShow;
      locationChangeRec.startTime = chosenPtDate;
      locationChangeRec.sortTime = chosenPtDate.dateTime;
      locationChangeRec.startTimeOffset = operationClone.cancelledTime
        ? operationClone.cancelledTimeOffset
        : operationClone.endTimeOffset;
      locationChangeRec.operationStartTime = operationClone.startTime;
      appData.dailyLogRecords = [locationChangeRec];

      let locationChangeSetupRecord = await this.setupService.getSetupChangeLogRecord(locationChangeRec.setupChangeLogApiId
        ? locationChangeRec.setupChangeLogApiId
        : locationChangeRec.localSetupChangeLogRecordId);

      if (locationChangeSetupRecord) {
        locationChangeSetupRecord = this.transitingSaveService.updateLocationChangeSetupRecord(locationChangeSetupRecord, operationClone, null, chosenPtDate, chosenTimeToShow);
        appData.setupChangeLogRecords = [locationChangeSetupRecord];
      }
    }

    return appData;
  }

  // opens the standard error modal
  public openErrorModal() {
    const errorModal = this.modalController.create(ErrorsComponent, {
      validationResult: this.validationResult,
      refresh: true
    });
    errorModal.present();
  }

  public async updateFieldPositioningFields() {
    normalizeFieldPositioningLegSummary(this.fieldPositioningEditor.legInfo);

    for (let legNumber = 1; legNumber <= 4; legNumber++) {
      const fieldPositioningLeg = ensureFieldPositioningLeg(this.fieldPositioningEditor.legInfo, legNumber);
      const finalJackingLeg = this.editor.finalJackingInfo[`leg${legNumber}`];

      if (!finalJackingLeg) {
        continue;
      }

      fieldPositioningLeg.penetration = finalJackingLeg.finalPenetration;
      fieldPositioningLeg.finalLegLoad = finalJackingLeg.load;
      fieldPositioningLeg.finalLegExt = finalJackingLeg.ext;
      fieldPositioningLeg.easting = finalJackingLeg.easting;
      fieldPositioningLeg.northing = finalJackingLeg.northing;
    }
  }

  private eventShouldUpdatePrimaryOperationStartTime(
    evnt: JackingActivityEventEditor
  ): boolean {
    // Arrival and start/start position event
    // Departure and (load/comm or start position event)
    if (
      (this.editor.type.id === JackingActivityTypes.Arrival &&
        (((this.vesselClass === 'NG14000X' || this.vesselClass === 'M') && evnt.description === 'Start') ||
          (this.vesselClass === 'NG2500X' &&
            evnt.description === 'Start Position') ||
          (this.vesselClass === 'NG5500X' &&
            evnt.description === 'Start Position'))) ||
      (this.editor.type.id === JackingActivityTypes.Departure &&
        (((this.vesselClass === 'NG14000X' || this.vesselClass === 'M') &&
            evnt.description === 'Load/Comm Lower') ||
          (this.vesselClass === 'NG2500X' &&
            evnt.description === 'Start Position') ||
          (this.vesselClass === 'NG5500X' &&
            evnt.description === 'Start Jacking Down Position')))
    ) {
      return true;
    } else {
      return false;
    }
  }

  // if this returns true, the event will be given a confirm tickbox, therefore for the new jacking arrival port/offshore operations
  // this should return false
  private eventShouldUpdatePrimaryOperationEndTime(
    evnt: JackingActivityEventEditor
  ): boolean {
    if (
      this.editor.type.id === JackingActivityTypes.Arrival &&
      // check secondary op == primary op, logic will differ based on if it's the old jacking arrival way or the new one
      this.secondaryOperation && this.secondaryOperation.id != this.primaryOperation.id &&
      (((this.vesselClass === 'NG14000X' || this.vesselClass === 'M') && evnt.description === 'Pin') ||
        (this.vesselClass === 'NG2500X' && evnt.description === 'Pinned') ||
        (this.vesselClass === 'NG5500X' && evnt.description === 'Pinned'))
    ) {
      return true;
    } else {
      return false;
    }
  }

  private eventShouldUpdateSecondaryOperationStartTime(
    evnt: JackingActivityEventEditor
  ): boolean {
    if (
      this.editor.type.id === JackingActivityTypes.Arrival &&
      // check secondary op == primary op, logic will differ based on if it's the old jacking arrival way or the new one
      this.secondaryOperation && this.secondaryOperation.id != this.primaryOperation.id &&
      (((this.vesselClass === 'NG14000X' || this.vesselClass === 'M') && evnt.description === 'Pin') ||
        (this.vesselClass === 'NG2500X' && evnt.description === 'Pinned') ||
        (this.vesselClass === 'NG5500X' && evnt.description === 'Pinned'))
    ) {
      return true;
    } else {
      return false;
    }
  }

  private eventShouldUpdateSecondaryOperationEndTime(
    evnt: JackingActivityEventEditor
  ): boolean {
    // Placeholder incase we need this functionality in the future.
    return false;
  }

  private async checkIfFinalValueShouldBeNextDay(finalValueTime: PtLuxonDate, finalValueTimeShow: string, finalValueIndex: number): Promise<PtLuxonDate> {
    // will either return an object with a ptDate, and a timeShow, that's all we need to decide whether to check if the given
    // date needs to be on the next day
    const previousValue = this.getLatestFinalValueInRelationToGivenIndex(finalValueIndex);

    if (previousValue && previousValue.timeShow && (await this.dateService.timeAllowed(previousValue.timeShow, previousValue.ptDate.getDate()))) {
      // compare using the UTC dates, offsets not needed
      if (this.dateService.isFirstDateLargerOffsetsPreApplied(previousValue.ptDate.dateTime, finalValueTime.dateTime)) {
        // if the event before the event being edited is larger, then we need to increment the edited event time
        finalValueTime = await this.dateService.incrementPtLuxonDateFromUserEnteredString(finalValueTimeShow, finalValueTime.dateTime);

        // Set max date if the new date is larger
        if (this.dateService.isFirstDayLargerOffsetsPreApplied(finalValueTime.dateTime, this.maxDate)) {
          this.maxDate = finalValueTime.dateTime;
        }
      }
    }

    return finalValueTime;
  }

  // recursively loop through previous final values (and then events if necessary) until we find one with a time that has been set
  private getLatestFinalValueInRelationToGivenIndex(finalValueIndex: number): { ptDate: PtLuxonDate, timeShow: string } {
    // order depends on vessel class
    // final values order - zaratan, leviathan, hydra, kraken (ng2500 and 5500 vessels)
    // 0: finalJackingInfoFinalPenetrationsTimeChange
    // 1: finalJackingInfoDifferenceFromExpectedTimeChange
    // 2: finalJackingInfoFinalValuesTimeChange
    // 3: finalJackingInfoDeepWellTieInDepthTimeChange
    // 4: finalJackingInfoDeepWellTieInNumberTimeChange
    // 5: finalJackingInfoFinalAirGapTimeChange
    // 6: finalJackingInfoDavitPumpUsedTimeChange

    // final values order - scylla - 14000x vessel class
    // 0: finalJackingInfoFinalValuesTimeChange
    // 1: finalJackingInfoDifferenceFromExpectedTimeChange
    // 2: finalJackingInfoFinalPenetrationsTimeChange
    // 3: finalJackingInfoSeajetsUsedTimeChange
    // 4: finalJackingInfoFinalAirGapTimeChange

    // combined order
    // 0/2 finalJackingInfoFinalPenetrationsTimeChange
    // 1 finalJackingInfoDifferenceFromExpectedTimeChange
    // 2/0 finalJackingInfoFinalValuesTimeChange
    // 3 finalJackingInfoSeajetsUsedTimeChange
    // 4 finalJackingInfoDeepWellTieInDepthTimeChange
    // 5 finalJackingInfoDeepWellTieInNumberTimeChange
    // 6 finalJackingInfoFinalAirGapTimeChange
    // 7 finalJackingInfoDavitPumpUsedTimeChange
    const indexCopy = cloneDeep(finalValueIndex);
    switch (indexCopy) {
      case 7: {
        return this.editor.finalJackingInfo.finalAirGapTime && this.editor.finalJackingInfo.finalAirGapTime.dateTime
          ? {
            ptDate: this.editor.finalJackingInfo.finalAirGapTime,
            timeShow: this.editor.finalJackingInfo.finalAirGapTimeToShow
          }
          : this.getLatestFinalValueInRelationToGivenIndex(indexCopy - 1);
      }
      case 6: {
        return this.editor.finalJackingInfo.deepWellTieInNumberTime && this.editor.finalJackingInfo.deepWellTieInNumberTime.dateTime
          ? {
            ptDate: this.editor.finalJackingInfo.deepWellTieInNumberTime,
            timeShow: this.editor.finalJackingInfo.deepWellTieInNumberTimeToShow
          }
          : this.getLatestFinalValueInRelationToGivenIndex(indexCopy - 1);
      }
      case 5: {
        return this.editor.finalJackingInfo.deepWellTieInDepthTime && this.editor.finalJackingInfo.deepWellTieInDepthTime.dateTime
          ? {
            ptDate: this.editor.finalJackingInfo.deepWellTieInDepthTime,
            timeShow: this.editor.finalJackingInfo.deepWellTieInDepthTimeToShow
          }
          : this.getLatestFinalValueInRelationToGivenIndex(indexCopy - 1);
      }
      case 4: {
        return this.editor.finalJackingInfo.seajetsUsedTime && this.editor.finalJackingInfo.seajetsUsedTime.dateTime
          ? {
            ptDate: this.editor.finalJackingInfo.seajetsUsedTime,
            timeShow: this.editor.finalJackingInfo.seajetsUsedTimeToShow
          }
          : this.getLatestFinalValueInRelationToGivenIndex(indexCopy - 1);
      }
      case 3: {
        if (this.vesselClass === 'NG14000X' || this.vesselClass === 'M') {
          return this.editor.finalJackingInfo.finalPenetrationsTime && this.editor.finalJackingInfo.finalPenetrationsTime.dateTime
            ? {
              ptDate: this.editor.finalJackingInfo.finalPenetrationsTime,
              timeShow: this.editor.finalJackingInfo.finalPenetrationsTimeToShow
            }
            : this.getLatestFinalValueInRelationToGivenIndex(indexCopy - 1);
        } else {
          return this.editor.finalJackingInfo.finalLegValuesTime && this.editor.finalJackingInfo.finalLegValuesTime.dateTime
            ? {
              ptDate: this.editor.finalJackingInfo.finalLegValuesTime,
              timeShow: this.editor.finalJackingInfo.finalLegValuesTimeToShow
            }
            : this.getLatestFinalValueInRelationToGivenIndex(indexCopy - 1);
        }
      }
      case 2: {
        return this.editor.finalJackingInfo.differenceFromExpectedTime && this.editor.finalJackingInfo.differenceFromExpectedTime.dateTime
          ? {
            ptDate: this.editor.finalJackingInfo.differenceFromExpectedTime,
            timeShow: this.editor.finalJackingInfo.differenceFromExpectedTimeToShow
          }
          : this.getLatestFinalValueInRelationToGivenIndex(indexCopy - 1);
      }
      case 1: {
        if (this.vesselClass === 'NG14000X' || this.vesselClass === 'M') {
          return this.editor.finalJackingInfo.finalLegValuesTime && this.editor.finalJackingInfo.finalLegValuesTime.dateTime
            ? {
              ptDate: this.editor.finalJackingInfo.finalLegValuesTime,
              timeShow: this.editor.finalJackingInfo.finalLegValuesTimeToShow
            }
            : this.getLatestFinalValueInRelationToGivenIndex(indexCopy - 1);
        } else {
          return this.editor.finalJackingInfo.finalPenetrationsTime && this.editor.finalJackingInfo.finalPenetrationsTime.dateTime
            ? {
              ptDate: this.editor.finalJackingInfo.finalPenetrationsTime,
              timeShow: this.editor.finalJackingInfo.finalPenetrationsTimeToShow
            }
            : this.getLatestFinalValueInRelationToGivenIndex(indexCopy - 1);
        }
      }
      case 0: {
        // either editing/checking the first finalValue, so now we need to cycle through each event
        for (let i = this.editor.events.length - 1; i >= 0; i--) {
          if (this.editor.events[i].time && this.editor.events[i].time.dateTime) {
            return {ptDate: this.editor.events[i].time, timeShow: this.editor.events[i].timeToShow};
          }
        }

        return null;
      }
    }
  }

  // aka the end time of a departure form
  private async checkIfLegsFreeTimeShouldBeNextDay() {
    const latestEventTime = this.getLatestEventTimeForLegsFreeCheck();

    if (latestEventTime.dateTime > this.editor.endDate.dateTime) {
      this.editor.endDate = await this.dateService.incrementDay(this.editor.endDate);
    }
  }

  // note that evnt.time.date will be a local time and match evnt.time.dateTime, not utc, after exiting this method
  private async checkIfEventShouldBeNextDay(evnt: JackingActivityEventEditor) {
    // Get index of current event
    let evntIndex = this.editor.events.indexOf(evnt);
    let previousEventFound = false;

    // Loop through events backwards to see if the 'time component'
    // of this event is less than the time component of the first time
    // found when looping backwards. If so this event should be the next day
    while (evntIndex > 0) {
      evntIndex--;
      const evntToCompare = this.editor.events[evntIndex];

      if (evntToCompare.timeToShow && (await this.dateService.timeAllowed(evntToCompare.timeToShow, evntToCompare.time.getDate()))) {
        previousEventFound = true;

        // compare using the UTC dates, offsets not needed
        if (this.dateService.isFirstDateLargerOffsetsPreApplied(evntToCompare.time.dateTime, evnt.time.dateTime)) {
          // if the event before the event being edited is larger, then we need to increment the edited event time
          evnt.time = await this.dateService.incrementPtLuxonDateFromUserEnteredString(evnt.timeToShow, evnt.time.dateTime);

          // Set max date if the new date is larger
          if (this.dateService.isFirstDayLargerOffsetsPreApplied(evnt.time.dateTime, this.maxDate)) {
            this.maxDate = evnt.time.dateTime;
          }

          // note that final position event is excluded from this check as it is supposed to come after the editor end time
          if (evnt.time.dateTime > this.editor.endDate.dateTime && evnt.description.toLowerCase() !== 'final position') {
            await this.updateEditorEndTime();
          }
        }

        break;
      }
    }

    if (!previousEventFound) {
      const baselineDate = this.dateService.isFirstDateLargerOffsetsPreApplied(this.editor.endDate.dateTime, this.editor.startDate.dateTime)
        ? this.editor.endDate.dateTime
        : this.editor.startDate.dateTime;

      if (this.dateService.isFirstDateLargerOffsetsPreApplied(baselineDate, evnt.time.dateTime)) {
        evnt.time = await this.dateService.incrementPtLuxonDateFromUserEnteredString(evnt.timeToShow, evnt.time.dateTime);

        if (this.dateService.isFirstDayLargerOffsetsPreApplied(evnt.time.dateTime, this.maxDate)) {
          this.maxDate = evnt.time.dateTime;
        }

        if (evnt.time.dateTime > this.editor.endDate.dateTime && evnt.description.toLowerCase() !== 'final position') {
          await this.updateEditorEndTime();
        }
      }
    }

    await this.calculateMinMaxDates();
  }

  private async updateEditorEndTime() {
    if (this.editor.type.id === JackingActivityTypes.Arrival) {
      this.editor.endDate = await this.dateService.incrementDay(this.editor.endDate);
      this.editor.showEndDate = await this.dateService.setFullTimeString(this.editor.endDate.dateTime);

      // final airgap is linked with the secondary op end time (i.e the editor.endDate), so update it here if the user has already entered it
      if (this.editor.finalJackingInfo.finalAirGapTimeToShow !== null && this.editor.finalJackingInfo.finalAirGapTimeToShow !== '') {
        this.editor.finalJackingInfo.finalAirGapTime = this.editor.endDate;
        this.editor.finalJackingInfo.finalAirGapTimeOffset = this.editor.endOffset;
      }

      if (this.secondaryOperation) {
        this.secondaryOperation.endTime = cloneDeep(this.editor.endDate);
        this.secondaryOperation.endTimeToShow = cloneDeep(this.editor.endTimeToShow);
        this.secondaryOperation.endTimeOffset = cloneDeep(this.editor.endOffset);

        if (this.secondaryOperation.id === this.primaryOperation.id) {
          this.cloneSecondaryEndTimeForPrimaryOperation();
        }
      }

      await this.toastCtrl.create({
        message: 'End Date Updated',
        duration: 4000,
        position: 'bottom'
      }).present();
    }

    if (this.editor.type.id === JackingActivityTypes.Departure) {
      this.primaryOperation.endTime = cloneDeep(this.editor.endDate);
      this.primaryOperation.endTimeToShow = cloneDeep(this.editor.endTimeToShow);
      this.primaryOperation.endTimeOffset = cloneDeep(this.editor.endOffset);

      await this.toastCtrl.create({
        message: 'Legs Free Time Updated',
        duration: 4000,
        position: 'bottom'
      }).present();
    }
  }

  private getLatestEventTimeForLegsFreeCheck(): PtLuxonDate {
    let latestDate = cloneDeep(this.editor.startDate);
    for (let i = 0; i < this.editor.events.length; i++) {
      const event = this.editor.events[i];

      // finalPosition, and 5m clear times, actually comes after the legs free time, even though the legs free time is linked to op end
      if (event.description.toLowerCase() == 'final position' || event.description.toLowerCase() == '5m clear') {
        continue;
      }

      if (event.time && event.time.dateTime && event.time.dateTime > latestDate.dateTime) {
        latestDate = cloneDeep(event.time);
      }
    }

    return latestDate;
  }

  private async calculateMinMaxDates() {
    let minDate = this.currentDate.dateTime > this.editor.startDate.dateTime ? this.currentDate.dateTime : this.editor.startDate.dateTime;
    let maxDate = this.editor.endDate && this.editor.endDate.dateTime ? this.editor.endDate.dateTime : await this.dateService.addDuration(minDate, {day: 1}, true);

    for (const evnt of this.editor.events) {
      if (evnt.time && evnt.time.dateTime) {
        maxDate = this.returnLargerDate(evnt.time.dateTime, maxDate);
        minDate = this.returnLesserDate(evnt.time.dateTime, minDate);
      }
    }

    // update min/max if necessary to ensure datepickers still show in final jacking data section
    maxDate = this.returnLargerDate(this.editor.finalJackingInfo.finalAirGapTime.dateTime, maxDate);
    maxDate = this.returnLargerDate(this.editor.finalJackingInfo.finalLegValuesTime.dateTime, maxDate);
    maxDate = this.returnLargerDate(this.editor.finalJackingInfo.differenceFromExpectedTime.dateTime, maxDate);
    maxDate = this.returnLargerDate(this.editor.finalJackingInfo.finalPenetrationsTime.dateTime, maxDate);
    maxDate = this.returnLargerDate(this.editor.finalJackingInfo.deepWellTieInDepthTime.dateTime, maxDate);
    maxDate = this.returnLargerDate(this.editor.finalJackingInfo.deepWellTieInNumberTime.dateTime, maxDate);
    maxDate = this.returnLargerDate(this.editor.finalJackingInfo.davitPumpUsedTime.dateTime, maxDate);
    maxDate = this.returnLargerDate(this.editor.finalJackingInfo.seajetsUsedTime.dateTime, maxDate);

    minDate = this.returnLesserDate(this.editor.finalJackingInfo.finalAirGapTime.dateTime, minDate);
    minDate = this.returnLesserDate(this.editor.finalJackingInfo.finalLegValuesTime.dateTime, minDate);
    minDate = this.returnLesserDate(this.editor.finalJackingInfo.differenceFromExpectedTime.dateTime, minDate);
    minDate = this.returnLesserDate(this.editor.finalJackingInfo.finalPenetrationsTime.dateTime, minDate);
    minDate = this.returnLesserDate(this.editor.finalJackingInfo.deepWellTieInDepthTime.dateTime, minDate);
    minDate = this.returnLesserDate(this.editor.finalJackingInfo.deepWellTieInNumberTime.dateTime, minDate);
    minDate = this.returnLesserDate(this.editor.finalJackingInfo.davitPumpUsedTime.dateTime, minDate);
    minDate = this.returnLesserDate(this.editor.finalJackingInfo.seajetsUsedTime.dateTime, minDate);

    this.maxDate = maxDate;
    this.minDate = minDate;

    this.activitySpansMultipleDays = !(await this.dateService.compareDayOffsetsPreApplied(minDate, maxDate));
  }

  returnLargerDate(date: DateTime, max: DateTime) {
    const isDateGTE = this.dateService.isFirstDateLargerOffsetsPreApplied(date, max);
    if (isDateGTE) {
      return date;
    } else {
      return max;
    }
  }

  returnLesserDate(date: DateTime, min: DateTime) {
    const isDateLTE = this.dateService.isFirstDateLargerOffsetsPreApplied(min, date);
    if (isDateLTE) {
      return date;
    } else {
      return min;
    }
  }

  private async checkIfTimeIsLost(
    timeInput: string,
    ptDate: PtLuxonDate,
    timeLabel: string,
    legName?: string
  ) {
    const lostInTime = ptDate.isLostInTime;

    const errIndex = this.validationResult.errors.findIndex(err =>
      err.message.includes(`${timeLabel}${legName ? ': ' + legName : ''}`)
    );
    if (errIndex !== -1) {
      this.validationResult.errors.splice(errIndex, 1);

      if (this.validationResult.errors.length === 0) {
        this.validationResult.isValid = true;
      }
    }

    if (lostInTime && lostInTime.result) {
      this.validationResult.addError(
        `The time '${timeInput}' entered for '${timeLabel}${
          legName ? ': ' + legName : ''
        }' is invalid due to a
        timezone change from '${lostInTime.timeZone.timeOfChangeToShow}' to '${
          lostInTime.timeZone.dateToShow
        }'.`,
        ErrorType.Error,
        ActionType.Timezone
      );
    }
  }

  // todo - why isn;t this used anywhere
  async evntOffsetSelected(eventDate: PtLuxonDate, event: JackingActivityEventEditor) {
    this.editor.startOffset = eventDate.dateTime.offset / 60;
  }

  private updateXMinHoldLogRecords(dailyLogRecords: DailyOperationLogRecord[]): DailyOperationLogRecord[] {
    for (let i = 0; i < dailyLogRecords.length; i++) {
      if (dailyLogRecords[i].operationName.toLowerCase().includes('comm') && dailyLogRecords[i].operationName.toLowerCase().includes('min holds')) {
        const event = this.editor.events.find(e => e.description === 'Comm. X min holds');

        if (event != null && event.holdMinutes !== null && event.holdMinutes !== undefined) {
          dailyLogRecords[i].operationName = 'Comm. ' + event.holdMinutes + ' min holds';
        } else if (event !== null) {
          dailyLogRecords[i].operationName = 'Comm. X min holds';
        }
      } else if (dailyLogRecords[i].operationName.toLowerCase().includes('comp') && dailyLogRecords[i].operationName.toLowerCase().includes('min holds')) {
        const event = this.editor.events.find(e => e.description === 'Comp. X min holds');

        if (event != null && event.holdMinutes !== null && event.holdMinutes !== undefined) {
          dailyLogRecords[i].operationName = 'Comp. ' + event.holdMinutes + ' min holds';
        } else if (event !== null) {
          dailyLogRecords[i].operationName = 'Comp. X min holds';
        }
      }
    }

    return dailyLogRecords;
  }

  // the event that corresponds to the operation start time may not be the actual first even, but instead will be the first event that contains
  // a time confirmation box
  startEventTimeEntered(): boolean {
    if (!this.editor || !this.editor.events || this.editor.events.length === 0) {
      return true;
    }

    if (this.editor.events.filter(e => e.showTimeConfirmation)[0].time.dateTime) {
      return true;
    }
  }

  private async presentValidationWarningToast() {
    let elapsedTime = 0;
    let durationMs = 4000;
    let intervalHandler = setInterval(() => {
      elapsedTime += 10;
    }, 10);

    const toast = this.toastCtrl.create({
      message: 'Validation Error',
      duration: durationMs,
      position: 'bottom',
      showCloseButton: true,
      closeButtonText: 'Click to View',
      dismissOnPageChange: true,
      cssClass: 'toast-error',
    });

    toast.onDidDismiss(() => {
      clearInterval(intervalHandler);
      if (elapsedTime < durationMs) {
        this.mainContent.scrollTop = 0;
        // feels less janky with the slight delay after the scroll
        setTimeout(() => {
          this.openErrorModal();
        }, 100)
      }
    });

    await toast.present();
  }

  lastLocationSelected(id: number) {
    this.editor.projectInfo.lastLocationId = id;
  }

  private async saveSecondaryOperationData(appDataBlob: AppData, secondaryOpClone: DailyLogOperationInstance, midnightPtLuxonDate: PtLuxonDate, editorClone: JackingActivityEditor) {
    // Declare midnight for secondary operation to split
    if (secondaryOpClone) {
      midnightPtLuxonDate = await this.dateService.getEndOfDayLocally(secondaryOpClone.startTime.getDate());

      // Split & Join Secondary Op Steps
      await this.stepService.splitStepsAtTime(secondaryOpClone, midnightPtLuxonDate, true);

      secondaryOpClone.switchTimes.forEach(split => {
        this.stepService.splitStepsAtTime(secondaryOpClone, split.date);
      });
      await this.stepService.attemptJoinStepsInOperation(
        secondaryOpClone
      );

      // Get new daily log records for Secondary Op
      const oldSecondaryOpDailyLogs = await this.appDataSyncService.getLogsForOperation(
        secondaryOpClone.id
      );

      const secondaryOpDailyLogs = await this.operationSaveService.saveTransitingOperation(
        secondaryOpClone,
        secondaryOpClone.stepCollectionInstance.stepInstances,
        secondaryOpClone.newLocationId,
        null,
        this.locationOptions,
        oldSecondaryOpDailyLogs,
        oldSecondaryOpDailyLogs.find(
          e => e.typeId === DailyLogType.OperationStart
        ).uniqueOrdinal - 1,
        secondaryOpClone,
        true
      );

      if (this.vesselClass === 'NG14000X' || this.vesselClass === 'M') {
        this.updateXMinHoldLogRecords(secondaryOpDailyLogs.records);
      }

      // if editor is cancelled, then this wil have been deleted above - remove it here so it doesn't get re-saved
      if (editorClone.cancellationTime && editorClone.cancellationTime.dateTime) {
        secondaryOpDailyLogs.records = secondaryOpDailyLogs.records.filter(e => e.typeId !== DailyLogType.OperationEnd);
      }

      appDataBlob.dailyLogRecords = appDataBlob.dailyLogRecords.concat(
        secondaryOpDailyLogs.records
      );

      // although opClone times are correct here, the timeToShow string has not been updated, so we need to calculate it again
      // (also, we set the string on the op instead of just the record, as we will pass this op into updateLocationChangeSetupRecord() below)
      secondaryOpClone.endTimeToShow = await this.dateService.setTimeStringFromDateTime(secondaryOpClone.endTime.dateTime);

      // Find location change records and adjust date
      const secondaryOpLocationChangeAppData = await this.tryUpdateLocationChangeRecord(secondaryOpClone, oldSecondaryOpDailyLogs, true);

      if (secondaryOpLocationChangeAppData && secondaryOpLocationChangeAppData.dailyLogRecords) {
        appDataBlob.dailyLogRecords = appDataBlob.dailyLogRecords.concat(secondaryOpLocationChangeAppData.dailyLogRecords);
      }

      if (secondaryOpLocationChangeAppData && secondaryOpLocationChangeAppData.setupChangeLogRecords) {
        appDataBlob.setupChangeLogRecords = appDataBlob.setupChangeLogRecords.concat(secondaryOpLocationChangeAppData.setupChangeLogRecords);
      }

      // Find transiting records and adjust date
      const secondaryLocationTransiting = oldSecondaryOpDailyLogs.find(
        e => e.typeId === DailyLogType.Transiting
      );

      if (secondaryLocationTransiting) {
        secondaryLocationTransiting.startTimeShow = secondaryOpClone.startTimeToShow;
        secondaryLocationTransiting.startTime = secondaryOpClone.startTime;
        secondaryLocationTransiting.sortTime = secondaryOpClone.startTime.dateTime;
        secondaryLocationTransiting.startTimeOffset = secondaryOpClone.startTimeOffset;
        secondaryLocationTransiting.operationStartTime = secondaryOpClone.startTime;

        let transitingToSetupRecord = await this.setupService.getSetupChangeLogRecord(secondaryLocationTransiting.setupChangeLogApiId
          ? secondaryLocationTransiting.setupChangeLogApiId
          : secondaryLocationTransiting.localSetupChangeLogRecordId);

        if (transitingToSetupRecord) {
          transitingToSetupRecord = this.transitingSaveService.updateTransitingToSetupRecord(transitingToSetupRecord, secondaryOpClone);
          appDataBlob.setupChangeLogRecords.push(transitingToSetupRecord);
        }

        appDataBlob.dailyLogRecords.push(secondaryLocationTransiting);
      }

      // add a second operation (arrival only)
      appDataBlob.dailyLogOperationInstances.push(secondaryOpClone);
    }
  }

  private async savePrimaryOperationData(appDataBlob: AppData, primaryOpClone: DailyLogOperationInstance, midnightPtLuxonDate: PtLuxonDate, editorClone: JackingActivityEditor) {
    // Split & Join Primary Op Steps
    await this.stepService.splitStepsAtTime(primaryOpClone, midnightPtLuxonDate, true);
    primaryOpClone.switchTimes.forEach(split => {
      this.stepService.splitStepsAtTime(
        primaryOpClone,
        split.date
      );
    });
    await this.stepService.attemptJoinStepsInOperation(primaryOpClone);

    // Get new daily log records for Primary Op
    const oldPrimaryOpDailyLogs = await this.appDataSyncService.getLogsForOperation(
      primaryOpClone.id
    );

    const primaryOpDailyLogs = await this.operationSaveService.saveTransitingOperation(
      primaryOpClone,
      primaryOpClone.stepCollectionInstance.stepInstances,
      primaryOpClone.newLocationId,
      null,
      this.locationOptions,
      oldPrimaryOpDailyLogs,
      oldPrimaryOpDailyLogs.find(
        e => e.typeId === DailyLogType.OperationStart
      ).uniqueOrdinal - 1,
      primaryOpClone,
      true
    );
    appDataBlob.dailyLogRecords = appDataBlob.dailyLogRecords.concat(
      primaryOpDailyLogs.records
    );

    // Find transiting records and adjust date
    const primaryLocationTransiting = oldPrimaryOpDailyLogs.find(
      e => e.typeId === DailyLogType.Transiting
    );
    if (primaryLocationTransiting) {
      primaryLocationTransiting.startTimeShow = primaryOpClone.startTimeToShow;
      primaryLocationTransiting.startTime = primaryOpClone.startTime;
      primaryLocationTransiting.sortTime = primaryOpClone.startTime.dateTime;
      primaryLocationTransiting.startTimeOffset = primaryOpClone.startTimeOffset;
      primaryLocationTransiting.operationStartTime = primaryOpClone.startTime;
      appDataBlob.dailyLogRecords.push(primaryLocationTransiting);

      let transitingToSetupRecord = await this.setupService.getSetupChangeLogRecord(primaryLocationTransiting.setupChangeLogApiId
        ? primaryLocationTransiting.setupChangeLogApiId
        : primaryLocationTransiting.localSetupChangeLogRecordId);

      if (transitingToSetupRecord) {
        transitingToSetupRecord = this.transitingSaveService.updateTransitingToSetupRecord(transitingToSetupRecord, primaryOpClone);
        appDataBlob.setupChangeLogRecords.push(transitingToSetupRecord);
      }
    }

    const primaryOpLocationChangeAppData = await this.tryUpdateLocationChangeRecord(primaryOpClone, oldPrimaryOpDailyLogs, true);

    if (primaryOpLocationChangeAppData && primaryOpLocationChangeAppData.dailyLogRecords) {
      appDataBlob.dailyLogRecords = appDataBlob.dailyLogRecords.concat(primaryOpLocationChangeAppData.dailyLogRecords);
    }

    if (primaryOpLocationChangeAppData && primaryOpLocationChangeAppData.setupChangeLogRecords) {
      appDataBlob.setupChangeLogRecords = appDataBlob.setupChangeLogRecords.concat(primaryOpLocationChangeAppData.setupChangeLogRecords);
    }

    appDataBlob.dailyLogOperationInstances.push(primaryOpClone);
  }

  private async validateJackingActivityEditor(editorClone: JackingActivityEditor) {
    await this.jackingActivityValidationService
      .ValidateWithAPI(editorClone)
      .then(
        res => {
          this.validationResult = res;
        },
        async err => {
          if (err.status && err.status === 412) {
            this.validationResult = err.error;
            await this.presentValidationWarningToast();
          }
        });

    if (this.validationResult.isValid && this.config.isImplementerEnvironment && !this.lockedDayPermissions.functionalPermissions.canImpactFutureDay) {
      await this.jackingActivityValidationService
        .ValidateLockedDayWithAPI(editorClone, cloneDeep(this.currentDate))
        .toPromise()
        .then(
          res => {
            this.validationResult = res;
          },
          err => {
            if (err.status && err.status === 412) {
              this.validationResult = err.error;
              this.mainContent.scrollTop = 0;
            }
          });
    }
  }

  // this is used to keep the primary op end times the same as secondary op if the ops are the same
  // this is necessary due to the save process only adding the primary operation to the appSaveData if the ops are equal,
  // as saving both ops when they are the same ends up overwriting step times, as the save process saves the primary op
  // data first (updated step times), and then secondary data (old step times) so ends up overwriting the new changes
  private cloneSecondaryEndTimeForPrimaryOperation() {
    this.primaryOperation.endTime = cloneDeep(this.secondaryOperation.endTime);
    this.primaryOperation.endTimeToShow = cloneDeep(this.secondaryOperation.endTimeToShow);
    this.primaryOperation.endTimeOffset = cloneDeep(this.secondaryOperation.endTimeOffset);
  }

  // will only create the field positioning report if the user has entered a CONFIRMED pinned time,
  // and if there isn't already an existing field positioning linked to. It should also not create the field positioning
  // if the user is aborting the jacking activity before the pinned time.
  private async createFieldPositioningEditor(appDataBlob: AppData) {
    // there should only ever be one field positioning linked to the jacking form
    if (this.fieldPositioningEditor || this.editor.type.name.toLowerCase() !== 'arrival') {
      return;
    }

    const pinnedEventIndex = this.editor.events.findIndex(e => e.description == this.getPinnedEventDescription());

    if (pinnedEventIndex !== -1) {
      const pinnedTimeAllowed = await this.dateService.timeAllowed(
        this.editor.events[pinnedEventIndex].timeToShow,
        this.editor.events[pinnedEventIndex].time.dateTime
      );

      if (pinnedTimeAllowed && this.editor.events[pinnedEventIndex].timeToShow !== null && this.editor.events[pinnedEventIndex].timeConfirmed) {
        if (this.editor.cancellationTime
          && this.editor.cancellationTime.dateTime
          && this.editor.cancellationTime.dateTime < this.editor.events[pinnedEventIndex].time.dateTime) {
          // field positioning only needs to be created if the pinned time has been reached before cancellation
          return;
        }

        this.fieldPositioningEditor = await this.fieldPositioningService
          .createOrUpdateActivityForOperation(this.primaryOperation, this.editor, this.editor.projectInfo.currentLocationId);

        if (this.fieldPositioningEditor) {
          this.fieldPositioningEditor.pinnedTime = cloneDeep(this.editor.events[pinnedEventIndex].time);
          this.fieldPositioningEditor.showPinnedTime = this.editor.events[pinnedEventIndex].timeToShow;

          await this.updateFieldPositioningFields();

          appDataBlob.fieldPositioningEditors = [this.fieldPositioningEditor];
        }
      }
    }
  }

  private getPinnedEventDescription(): string {
    switch (this.vesselClass) {
      case 'NG14000X':
      case 'M':
        return 'Pin';
      case 'NG5500X':
      case 'NG2500X':
        return 'Pinned';
      default:
        return 'Pinned';
    }
  }

  private async checkCanUploadFiles() {
    this.canUploadFiles = true;

    // if cancelled then can only upload files if it was cancelled after the pinned time
    if (this.editor.cancellationTime && this.editor.cancellationTime.dateTime) {
      const pinnedEventIndex = this.editor.events.findIndex(e => e.description == this.getPinnedEventDescription());

      if (pinnedEventIndex !== -1) {
        const pinnedTimeAllowed = await this.dateService.timeAllowed(
          this.editor.events[pinnedEventIndex].timeToShow,
          this.editor.events[pinnedEventIndex].time.dateTime
        );

        if (!pinnedTimeAllowed || this.editor.events[pinnedEventIndex].timeToShow === null) {
          this.canUploadFiles = false;
        } else if (pinnedTimeAllowed && this.editor.events[pinnedEventIndex].timeToShow !== null && this.editor.events[pinnedEventIndex].timeConfirmed) {
          if (this.editor.cancellationTime
            && this.editor.cancellationTime.dateTime
            && this.editor.cancellationTime.dateTime < this.editor.events[pinnedEventIndex].time.dateTime) {

            this.canUploadFiles = false;
          }
        }
      }
    }
  }

  setDeepWellTieInNotesShow() {
    this.showDeepWellTieInNotes = this.editor.projectInfo.deepWellTieInsUsed.some(e => e.name.toLowerCase() === 'other');
  }
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v1/jacking-activity-edit/jacking-activity-edit.ts